import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import ChangePriceDialog from './ChangePriceDialog';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import {
  handlePasteOnlyNumbers,
  validateDecimalNumer,
  validateNoSpecialChars,
  validateOnlyNumbers,
  validateOnlyNumbersNotThree,
} from '../../validation/ValidationInput';
import PopupLocation from '../../components/PopupLocation';
import {
  createProduct,
  getCourses,
  getAssetsCategory,
  getDetailProduct,
  getGroupProduct,
  getListSystemAccount,
  getQuickAddDefaults,
  getListUOM,
  getListUOMAlt,
  getSockHolding,
  getTaxGroupsForProductPrices,
  getPrintGroups,
  updateProduct,
} from '../../services/items.services';
import PlusGreenIcon from '../../images/PlusGreenIcon';
import RedDeleteIconV2 from '../../images/RedDeleteIconV2';
import { getListAttributes } from '../../services/attributes.service';
import {
  ALLERGEN_LEVEL,
  getListAllergens,
  type Allergen,
  type AllergenLevel,
  type ProductAllergen,
} from '../../services/allergens.service';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from '../../services/fieldLimits';
import FieldCounter from '../../components/FieldCounter';
import UploadIcon from '../../images/UploadIcon';
import CloseIcon from '../../images/CloseIcon';
import BackIcon from '../../images/BackIcon';
import CloseIconV2 from '../../images/CloseIconV2';
import { DropdownSearch } from '../../components/DropdownSearch';
import DeleteIconV2 from '../../images/DeleteIconV2';
import EditIconV2 from '../../images/EditIconV2';
import { useResponsive } from '../../hook/useResponsive';
import { useDialogFocus, useFormKeyboard } from '../../hook/useFormKeyboard';
import CheckIcon from '../../images/CheckIcon';
import EditIcon from '../../images/EditIcon';
import { PriceType, PRICE_TYPE_OPTIONS } from '../../enums/price-type.enums';

// The step showing each field's error, so a save error jumps to it.
const ITEM_FIELD_STEPS: Record<string, number> = {
  sku: 1, productGroupId: 1, productName: 1, type: 1, baseUOMId: 1, displayName: 1,
};

const CreateItems = () => {
  const [idSelect, setIdSelect] = useState('');
  const limits = useFieldLimits(inventoryFormLimits('item'));
  const [formValues, setFormValues] = useState<any>({
    productGroupId: '',
    // Optional: blank = the API allocates the next QA-NNNNN. Initialised so
    // the input is controlled from the first render.
    sku: '',
    productName: '',
    type: '',
    baseUOMId: '',
    courseId: null as string | null,
    alternativeIds: [],
    productConfigurationData: {
      isCountable: true,
      print_group_ids: [] as string[],
    },
  });
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const hasLoadedPageRef = useRef(new Set<number>());
  const hasLoadedPageGroupRef = useRef(new Set<number>());
  const hasLoadedPageUOMRef = useRef(new Set<number>());
  const hasLoadedPageAttributesRef = useRef(new Set<number>());
  const [openDropdownIndexes, setOpenDropdownIndexes] = useState<{ [key: number]: boolean }>({});
  const [listPrintGroups, setListPrintGroups] = useState<{ id: string; name: string; code: string; print_document_type_code?: string }[]>([]);
  const [searchTaxMap, setSearchTaxMap] = useState<any>({});
  const listType = [
    {
      value: 1,
      name: 'All',
    },
    {
      value: 2,
      name: 'Stock Item',
    },
    {
      value: 3,
      name: 'Sale Item',
    },
    {
      value: 4,
      name: 'Asset',
    },
  ];
  const [showPopupImage, setShowPopupImage] = useState<boolean>(false);
  const [barcodes, setBarcodes] = useState('');
  const preLanguage = localStorage.getItem('language');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [contractExpiration, setContractExpiration] = useState<Date | null>(null);
  const [filter, setFilter] = useState<any>({
    page: 1,
    limit: 50,
    sort: 'DESC',
  });
  const initialFilter = {
    searchValue: '',
    page: 1,
    searchValueProduct: '',
  };
  const [alternativeId, setAlternativeId] = useState({
    allowOrdering: false,
    allowRecipe: false,
    UOMId: '',
    uomName: '',
  });
  const [filterStock, setFilterStock] = useState(initialFilter);
  const [filterGroup, setFilterGroup] = useState(initialFilter);
  const [filterUOM, setFilterUOM] = useState(initialFilter);
  const [filterAttributes, setFilterAttributes] = useState({ page: 1, searchValue: '' });
  const [step, setStep] = useState<number>(1);
  const { isMobile, getWidth } = useResponsive();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [fileUpload, setFileUpload] = useState<any>('');
  const [previewImg, setPreviewImg] = useState<any>('');
  const [listSystem, setListSystem] = useState<any>([]);
  const [groupItems, setGroupItems] = useState<any>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [listUOM, setListUOM] = useState<any>([]);
  const [listUOMConfiguration, setListUOMConfiguration] = useState<any>([]);
  const [listUOMAlt, setListUOMAlt] = useState<any>([]);
  const [listCourses, setListCourses] = useState<any[]>([]);
  const [searchCourse, setSearchCourse] = useState('');
  const [isCourseDropdownOpen, setIsCourseDropdownOpen] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [searchGroupItems, setSearchGroupItems] = useState('');
  const [isGroupItemsDropdownOpen, setIsGroupItemsDropdownOpen] = useState(false);
  const dropdownGroupRef = useRef<HTMLDivElement>(null);
  // Prominent "Selling" card (create flow): drives the single price row.
  const sellingTaxRef = useRef<HTMLDivElement>(null);
  const [isSellingTaxOpen, setIsSellingTaxOpen] = useState(false);
  // Collapsed-by-default 'Advanced options' (availability + accounting) so the
  // create flow stays light; power users expand when they need it.
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Edit mode: simple Change Price dialog + collapsed-by-default history table.
  const [showChangePrice, setShowChangePrice] = useState(false);
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [searchStockItems, setSearchStockItems] = useState('');
  const [searchIdStockItems, setSearchIdStockItems] = useState('');
  const [searchIdUOMRecpit, setSearchIdUOMRecpit] = useState('');
  const [isStockItemsDropdownOpen, setIsStockItemsDropdownOpen] = useState(false);
  const dropdownStockRef = useRef<HTMLDivElement>(null);
  const [searchAttributesItems, setSearchAttributesItems] = useState('');
  const [isAttributesItemsDropdownOpen, setIsAttributesItemsDropdownOpen] = useState(false);
  const dropdownAttributesRef = useRef<HTMLDivElement>(null);
  const [searchTaxItems, setSearchTaxItems] = useState('');
  const [isTaxItemsDropdownOpen, setIsTaxItemsDropdownOpen] = useState(false);
  const dropdownTaxRef = useRef<HTMLDivElement>(null);
  const [searchIncome, setSearchIncome] = useState('');
  const [isIncomeDropdownOpen, setIsIncomeDropdownOpen] = useState(false);
  const dropdownIncomeRef = useRef<HTMLDivElement>(null);
  const assetCategoryRef = useRef<HTMLDivElement>(null);
  const [assetCategory, setAssetCategory] = useState<string>('');
  const [isAssetCategoryDropdownOpen, setIsAssetCategoryDropdownOpen] = useState(false);
  const [listAssetCategory, setListAssetCategory] = useState<any[]>([]);
  const [searchExpense, setSearchExpense] = useState('');
  const [isExpenseDropdownOpen, setIsExpenseDropdownOpen] = useState(false);
  const dropdownExpenseRef = useRef<HTMLDivElement>(null);
  const [searchType, setSearchType] = useState('');
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const dropdownTypeRef = useRef<HTMLDivElement>(null);
  const dropdownCourseRef = useRef<HTMLDivElement>(null);
  const [searchUOM, setSearchUOM] = useState('');
  const [searchUOMAlt, setSearchUOMAlt] = useState('');
  const [searchUOMRecipt, setSearchUOMRecipt] = useState('');
  const [isUOMDropdownOpen, setIsUOMDropdownOpen] = useState(false);
  const [isUOMDropdownReciptOpen, setIsUOMDropdownReciptOpen] = useState(false);
  const [isUOMAltDropdownOpen, setIsUOMAltDropdownOpen] = useState(false);
  const dropdownUOMRef = useRef<HTMLDivElement>(null);
  const dropdownReciptUOMRef = useRef<HTMLDivElement>(null);
  const dropdownUOMAltRef = useRef<HTMLDivElement>(null);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const calculatedWidth = windowWidth / 1290;
  const path = window.location.pathname;
  const [listTax, setListTax] = useState<any>([]);
  const [listStock, setListStock] = useState<any>([]);
  const [listAttributes, setListAttributes] = useState<any>([]);
  const [listAttributesSelect, setListAttributesSelect] = useState<any>([]);
  // Allergens: the fixed global list, plus this product's current selection
  // keyed by allergenId. Assigning allergens is entirely optional.
  const [listAllergens, setListAllergens] = useState<Allergen[]>([]);
  const [selectedAllergens, setSelectedAllergens] = useState<Record<string, AllergenLevel>>({});
  // Set only when fetchData actually reached the allergen prefill. Submit
  // OMITS the allergens key unless this ran (or the item is new): the API
  // treats an absent array as "leave assignments untouched", so a partial
  // page load can never silently clear a product's allergen declarations.
  const allergensPrefilledRef = useRef(false);
  const dropdownRefs = useRef<(HTMLDivElement | null)[]>([]);
  const taxInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const taxDropdownPortalRef = useRef<HTMLDivElement>(null);
  const priceSectionRef = useRef<HTMLDivElement>(null);
  const [taxDropdownPosition, setTaxDropdownPosition] = useState<{ index: number; top: number; left: number; width: number } | null>(null);
  const [language, setLanguage] = useState<any>(null);
  const [isDisplayNameCustomized, setIsDisplayNameCustomized] = useState(false);
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');
  const id: string = path.split('/').pop() || '';
  const kb = useFormKeyboard({ step, autoFocus: id === 'new' && !isMobile });
  const recipeKb = useFormKeyboard();
  const barcodeKb = useFormKeyboard();
  const altUomKb = useFormKeyboard();
  const deleteUomKb = useFormKeyboard();
  // Keyboard highlight for the hand-rolled purchase-tax and asset-category lists.
  const [pickerHighlight, setPickerHighlight] = useState<{ key: string; index: number }>({ key: '', index: -1 });
  // Country-scoped defaults (country_quick_add_defaults): product type, base
  // UOM, sales/purchase tax, income/expense codes. Fetched once for new items;
  // a 404 (no row for the country) leaves everything manual.
  const [countryDefaults, setCountryDefaults] = useState<any>(null);
  useEffect(() => {
    if (id !== 'new') return;
    let active = true;
    getQuickAddDefaults()
      .then((res: any) => {
        // Endpoint wraps as { status, data: {...} }; service wraps again as
        // { data: <body> }. The resolved defaults are the inner object.
        if (active) setCountryDefaults(res?.data?.data ?? res?.data ?? null);
      })
      .catch(() => {
        if (active) setCountryDefaults(null);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  // On create, the "Selling" card captures a single price + tax. Seed one price
  // row (cost 0, regular, 24-month validity) so the existing save/validation
  // path is satisfied without exposing the full historical-price table. Stock
  // Items carry no price, so clear any seeded row when switching to that type.
  useEffect(() => {
    if (id !== 'new') return;
    if (searchType === 'Stock Item') {
      setRows((prev: any[]) => (prev.length ? [] : prev));
      return;
    }
    setRows((prev: any[]) =>
      prev.length
        ? prev
        : [
            {
              price: '',
              cost: 0,
              validFrom: new Date(),
              validTo: getDateAfterMonths(24),
              isActive: true,
              priceType: PriceType.Regular,
            },
          ],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, searchType]);
  const [listBarcode, setListBarcode] = useState<any>([]);
  const [hasMore, setHasMore] = useState({
    attributes: true,
    tax: true,
    product: true,
    group: true,
    uom: true,
    assetCategory: true,
    systemAccount: true,
  });
  const [loading, setLoading] = useState({
    attributes: true,
    tax: true,
    product: true,
    group: true,
    uom: true,
    assetCategory: true,
    systemAccount: true,
  });

  const getDateAfterMonths = (months: number) => {
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    return date;
  };

  const getDateAfterMonthsFrom = (base: Date, months: number) => {
    const date = new Date(base);
    date.setMonth(date.getMonth() + months);
    return date;
  };

  const normalizePriceDay = (d: any): Date | null => {
    if (d == null) return null;
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return null;
    return new Date(x.getFullYear(), x.getMonth(), x.getDate());
  };

  /** Inclusive day ranges [from, to] overlap if they share at least one calendar day. */
  const pricePeriodsOverlapInclusive = (
    fromA: Date,
    toA: Date,
    fromB: Date,
    toB: Date
  ): boolean => {
    const a0 = fromA.getTime();
    const a1 = toA.getTime();
    const b0 = fromB.getTime();
    const b1 = toB.getTime();
    return a0 <= b1 && b0 <= a1;
  };

  /**
   * Giữ thứ tự dòng; nếu một dòng chồng kỳ (ít nhất một ngày trùng) với bất kỳ dòng nào đã giữ trước đó thì bỏ dòng đó khỏi mảng.
   */
  const removeOverlappingPriceRows = (priceRows: any[]) => {
    // Compare the RAW instants, not local calendar days. The previous
    // normalizePriceDay() read LOCAL date parts off UTC-stored timestamps, so an
    // end-of-day valid_to (…T23:59:59.999Z) rolled into the NEXT calendar day in
    // positive-UTC timezones (e.g. UK/BST) and falsely "overlapped" the following
    // price's start (…T00:00:00.000Z). That false overlap silently dropped a price
    // the user never deleted, and the backend then soft-deleted it (delete-by-
    // omission). Instant comparison keeps adjacent [ …23:59:59.999Z | next-day
    // 00:00:00.000Z ] windows non-overlapping in every timezone.
    const asDate = (d: any): Date | null => {
      if (d == null) return null;
      const x = new Date(d);
      return Number.isNaN(x.getTime()) ? null : x;
    };
    const kept: any[] = [];
    let removedCount = 0;
    for (const row of priceRows) {
      const f = asDate(row?.validFrom);
      const t = asDate(row?.validTo);
      if (!f || !t) {
        kept.push(row);
        continue;
      }
      if (f.getTime() > t.getTime()) {
        kept.push(row);
        continue;
      }
      let overlapsKept = false;
      for (const k of kept) {
        const kf = asDate(k?.validFrom);
        const kt = asDate(k?.validTo);
        if (!kf || !kt || kf.getTime() > kt.getTime()) continue;
        if (pricePeriodsOverlapInclusive(f, t, kf, kt)) {
          overlapsKept = true;
          break;
        }
      }
      if (overlapsKept) {
        removedCount += 1;
      } else {
        kept.push(row);
      }
    }
    return { keptRows: kept, removedCount };
  };
  const [showAlt, setShowAlt] = useState<boolean>(false);
  const PAGE_SIZE = 30;
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideTaxPortal = taxDropdownPortalRef.current?.contains(target);
      dropdownRefs.current.forEach((ref, idx) => {
        if (ref && !ref.contains(target) && !isInsideTaxPortal) {
          setOpenDropdownIndexes(prev => ({ ...prev, [idx]: false }));
        }
      });
      if (dropdownGroupRef.current && !dropdownGroupRef.current.contains(event.target as Node)) {
        setIsGroupItemsDropdownOpen(false);
      }
      if (dropdownStockRef.current && !dropdownStockRef.current.contains(event.target as Node)) {
        setIsStockItemsDropdownOpen(false);
      }
      if (
        dropdownAttributesRef.current &&
        !dropdownAttributesRef.current.contains(event.target as Node)
      ) {
        setIsAttributesItemsDropdownOpen(false);
      }
      if (dropdownTaxRef.current && !dropdownTaxRef.current.contains(event.target as Node)) {
        setIsTaxItemsDropdownOpen(false);
      }
      if (dropdownUOMRef.current && !dropdownUOMRef.current.contains(event.target as Node)) {
        setIsUOMDropdownOpen(false);
      }
      if (
        dropdownReciptUOMRef.current &&
        !dropdownReciptUOMRef.current.contains(event.target as Node)
      ) {
        setIsUOMDropdownReciptOpen(false);
      }
      if (dropdownUOMAltRef.current && !dropdownUOMAltRef.current.contains(event.target as Node)) {
        setIsUOMAltDropdownOpen(false);
      }

      if (dropdownTypeRef.current && !dropdownTypeRef.current.contains(event.target as Node)) {
        setIsTypeDropdownOpen(false);
      }
      if (
        dropdownExpenseRef.current &&
        !dropdownExpenseRef.current.contains(event.target as Node)
      ) {
        setIsExpenseDropdownOpen(false);
      }
      if (dropdownIncomeRef.current && !dropdownIncomeRef.current.contains(event.target as Node)) {
        setIsIncomeDropdownOpen(false);
      }
      if (assetCategoryRef.current && !assetCategoryRef.current.contains(event.target as Node)) {
        setIsAssetCategoryDropdownOpen(false);
      }
      if (dropdownCourseRef.current && !dropdownCourseRef.current.contains(event.target as Node)) {
        setIsCourseDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update TAX dropdown position when open (for portal) and on scroll/resize
  useEffect(() => {
    const openIndex = Object.keys(openDropdownIndexes).find(i => openDropdownIndexes[Number(i)]);
    if (openIndex === undefined) {
      setTaxDropdownPosition(null);
      return;
    }
    const idx = Number(openIndex);
    const updatePosition = () => {
      const el = taxInputRefs.current[idx];
      if (el) {
        const r = el.getBoundingClientRect();
        setTaxDropdownPosition({ index: idx, top: r.bottom, left: r.left, width: Math.max(r.width, 200) });
      }
    };
    updatePosition();
    const scrollEl = priceSectionRef.current;
    window.addEventListener('resize', updatePosition);
    scrollEl?.addEventListener('scroll', updatePosition);
    return () => {
      window.removeEventListener('resize', updatePosition);
      scrollEl?.removeEventListener('scroll', updatePosition);
    };
  }, [openDropdownIndexes]);

  const getListAssetCategories = async (categoryId: any | null) => {
    setLoading(prev => ({ ...prev, assetCategory: true }));
    try {
      const response: any = await getAssetsCategory();
      setListAssetCategory(response.data.data);
      if (id !== 'new') {
        const category: any = response.data.data.find((value: any) => value.id === categoryId);
        setFormValues((prev: any) => ({
          ...prev,
          assetCategoryId: category?.id,
        }));
        setAssetCategory(category?.categoryName);
      }
    } catch (error) {
      console.error('Error loading terms:', error);
    } finally {
      setLoading(prev => ({ ...prev, assetCategory: false }));
      setHasMore(prev => ({ ...prev, assetCategory: false }));
    }
  };
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  useEffect(() => {
    if (id !== 'new') return;
    const today = new Date();
    const offSale = getDateAfterMonths(24);
    setStartDate(today);
    setContractExpiration(offSale);
    setFormValues((prev: any) => ({
      ...prev,
      onSaleDate: prev?.onSaleDate ?? today,
      offSaleDate: prev?.offSaleDate ?? offSale,
      maxDiscount: prev?.maxDiscount ?? 100,
    }));
  }, [id]);
  const loadData = async () => {
    if (id !== 'new') {
      await fetchData(id);
    }
    const [[groups, categories, list, uomBase], [accounts, attributes, taxes], coursesRes, allergensRes] = await Promise.all([
      Promise.all([
        onLoadMoreGroup(),
        onLoadMoreUOM(),
        getListAssetCategories(''),
        onLoadMoreStock(),
      ]),
      Promise.all([getSystemAccount(), bootstrapAttributes(), getListTax()]),
      getCourses(true).then((r: any) => r?.data?.data ?? []).catch(() => []),
      // Failure tolerated: the Allergens card simply doesn't render, and the
      // submit gate keeps an unrendered card from clearing saved assignments.
      getListAllergens().then((r: any) => r?.data?.data?.allergens ?? []).catch(() => []),
    ]);
    setListCourses(Array.isArray(coursesRes) ? coursesRes : []);
    setListAllergens(Array.isArray(allergensRes) ? allergensRes : []);
    try {
      const groups = await getPrintGroups();
      const list = Array.isArray(groups) ? groups : [];
      setListPrintGroups(list);
      if (id === 'new' && list.length > 0) {
        // Default a new item to print to the Receipt destination only (not every
        // destination). Match on name / code / document-type containing
        // "receipt"; if none resolves, default to no destination.
        const receipt = list.find((g: any) =>
          [g?.name, g?.code, g?.print_document_type_code].some(
            (v: any) => typeof v === 'string' && v.toLowerCase().includes('receipt'),
          ),
        );
        setFormValues((prev: any) => ({
          ...prev,
          productConfigurationData: {
            ...prev.productConfigurationData,
            print_group_ids: receipt?.id ? [receipt.id] : [],
          },
        }));
      }
    } catch {
      setListPrintGroups([]);
    }
  };

  useEffect(() => {
    loadData();
    const handleNewToken = () => {
      loadData();
    };
    document.addEventListener('newToken', handleNewToken);
    return () => {
      document.removeEventListener('newToken', handleNewToken);
    };
  }, []);
  const getListTax = async () => {
    setLoading(prev => ({ ...prev, tax: true }));
    try {
      const groups = await getTaxGroupsForProductPrices();
      setListTax(Array.isArray(groups) ? groups : []);
    } catch (error) {
      console.error('Error loading tax groups:', error);
    } finally {
      setLoading(prev => ({ ...prev, tax: false }));
      setHasMore(prev => ({ ...prev, tax: false }));
    }
  };
  const getSystemAccount = async (id?: any, type?: any) => {
    setLoading(prev => ({ ...prev, systemAccount: true }));
    try {
      const response = await getListSystemAccount();
      const payload = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];
      const normalized = payload
        .filter((item: any) => item?.isDeleted !== true && item?.isActive !== false)
        .map((item: any) => ({
          ...item,
          accountCode: item?.accountCode ?? item?.systemAccountCode ?? '',
          accountName: item?.accountName ?? item?.systemAccountName ?? '',
        }));
      setListSystem(normalized);
    } catch (error) {
      console.error('Error loading terms:', error);
    } finally {
      setLoading(prev => ({ ...prev, systemAccount: false }));
      setHasMore(prev => ({ ...prev, systemAccount: false }));
    }
  };
  /** Display text for tax group: tax_groups.group_name only (list + selected row). */
  const taxGroupDisplayName = (tax: any): string => {
    if (!tax || typeof tax !== 'object') return '';
    return String(tax.groupName ?? tax.group_name ?? '').trim();
  };
  const fetchData = async (id: string) => {
    webLoadingTrue();
    try {
      const response: any = await getDetailProduct(id, companyId);
      // The detail API joins productGroup / productExpenseCode /
      // productIncomeCode / productPrices / attributes / recipeData via LEFT
      // JOINs, so any of them can come back as `null` (or `undefined`) for
      // legitimate data states — e.g. an item with no charts-of-accounts
      // mapping yet, no recipe, no attributes, or a product_group that has
      // since been deleted. The previous version of this fn assumed every
      // one of those was populated and chained into them directly
      // (`response.data.productGroup.groupName`,
      // `productExpenseCode.chartsOfAccount.accountCode`, etc.). When one
      // of them was actually null the chained access threw a TypeError,
      // the outer `catch` swallowed it with a console.error, and the next
      // ~9 setters (status / prices / on-sale + off-sale dates / tax-group
      // labels / attributes / asset category / course) silently never ran
      // — leaving the UI with five blank fields the user reported as
      // "not loading". Each access below now uses optional chaining +
      // explicit fallbacks so a missing relation degrades a single field
      // instead of aborting the whole population pass.
      const data = response?.data ?? {};
      const { productIncomeCode, productExpenseCode, ...rest } = data;
      // Prefill allergens FIRST, before any further await: everything below
      // can throw into the outer catch, and the submit gate on
      // allergensPrefilledRef must only open when this state is real.
      const productAllergens: any[] = Array.isArray(data.allergens)
        ? data.allergens
        : [];
      setSelectedAllergens(
        productAllergens.reduce(
          (acc: Record<string, AllergenLevel>, val: any) => {
            if (val?.allergenId) acc[val.allergenId] = val.level;
            return acc;
          },
          {}
        )
      );
      allergensPrefilledRef.current = true;
      const productUOMs: any[] = Array.isArray(data.productUOMs)
        ? data.productUOMs
        : [];
      const baseUOM = productUOMs.find((v: any) => v?.isBase);
      if (baseUOM?.uom?.id) {
        await fetchUOMAltList(baseUOM.uom.id);
      }
      const pcRaw = rest.productConfigurationData || {};
      const printGroupIdsFromApi =
        Array.isArray(pcRaw.print_groups) && pcRaw.print_groups.length > 0
          ? pcRaw.print_groups.map((g: any) => g.id).filter(Boolean)
          : Array.isArray(pcRaw.print_group_ids)
            ? pcRaw.print_group_ids
            : [];
      const recipeData: any[] = Array.isArray(data.recipeData)
        ? data.recipeData
        : [];
      setFormValues({
        ...rest,
        productConfigurationData: {
          isCountable: pcRaw.isCountable !== false,
          purchaseTaxId: pcRaw.purchaseTaxId,
          print_group_ids: printGroupIdsFromApi,
        },
        chartsOfAccountsExpenseId:
          data.productExpenseCode?.chartsOfAccountsId ?? '',
        chartsOfAccountsIncomeId:
          data.productIncomeCode?.chartsOfAccountsId ?? '',
        baseUOMId: baseUOM ? baseUOM?.UOMId : '',
        alternativeIds: productUOMs
          .filter((v: any) => !v?.isBase)
          .map((val: any) => ({
            uomName: val?.uom?.uomName ?? '',
            allowOrdering: val?.allowOrdering,
            allowRecipe: val?.allowRecipe,
            UOMId: val?.UOMId,
          })),
        recipeData: recipeData
          .filter((v: any) => !v?.isBase)
          .map((val: any) => ({
            ...val,
            uomName: val?.productUOM?.uom?.uomName ?? '',
          })),
      });
      setListBarcode(rest?.barcodes ?? []);
      setSearchUOM(baseUOM?.uom?.uomName ?? '');
      setSearchGroupItems(data.productGroup?.groupName ?? '');
      setSearchType(
        listType
          .filter((v: any) => v.value === data.type)
          .map((v: any) => String(v.name))
          .join('')
      );
      const productPrices: any[] = Array.isArray(data.productPrices)
        ? data.productPrices
        : [];
      setRows(productPrices);
      // Expense/income display strings: only build the "code - name" label
      // when the joined chartsOfAccount actually came back; otherwise emit
      // an empty string so the UI input renders blank rather than crashing
      // on `null.accountCode`.
      const expenseCoa = data.productExpenseCode?.chartsOfAccount;
      setSearchExpense(
        expenseCoa
          ? `${expenseCoa.accountCode ?? ''} - ${expenseCoa.accountName ?? ''}`
          : ''
      );
      const incomeCoa = data.productIncomeCode?.chartsOfAccount;
      setSearchIncome(
        incomeCoa
          ? `${incomeCoa.accountCode ?? ''} - ${incomeCoa.accountName ?? ''}`
          : ''
      );
      setStartDate(data.onSaleDate ? new Date(data.onSaleDate) : null);
      setContractExpiration(
        data.offSaleDate ? new Date(data.offSaleDate) : null
      );
      const groups = await getTaxGroupsForProductPrices();
      const groupNameById = new Map<string, string>();
      for (const g of groups ?? []) {
        const name = String((g as any).groupName ?? '').trim();
        groupNameById.set(String((g as any).id), name);
      }
      const result = productPrices.reduce(
        (acc: { [key: string]: string }, item: any, index: number) => {
          acc[index.toString()] =
            groupNameById.get(String(item?.taxGroupId ?? '')) ||
            taxGroupDisplayName(item?.tax);
          return acc;
        },
        {}
      );
      setSearchTaxMap(result);
      {
        const pc = data.productConfigurationData;
        const purchaseTaxId = pc?.purchaseTaxId;
        const labelFromApi = String(pc?.taxGroup?.groupName ?? '').trim();
        const labelFromList =
          purchaseTaxId &&
          (groups as any[])?.find((g: any) => String(g.id) === String(purchaseTaxId));
        const label =
          labelFromApi ||
          (labelFromList ? String(labelFromList.groupName ?? '').trim() : '') ||
          '';
        setSearchTaxItems(label);
      }
      const attributes: any[] = Array.isArray(data.attributes)
        ? data.attributes
        : [];
      setListAttributesSelect(
        attributes.map((val: any) => ({
          ...val,
          id: val?.attributeId,
          isSelect: true,
        }))
      );
      setAssetCategory(data.assetCategory?.categoryName);
      setSearchCourse(data.course?.name ?? '');
    } catch (error) {
      console.error('Error loading terms:', error);
    } finally {
      webLoadingFalse();
    }
  };
  const fetchAttributesList = async (
    filterAttrs: { page: number; searchValue: string },
    pageSize: number,
    isAppend: boolean = true
  ) => {
    try {
      setLoading(prev => ({ ...prev, attributes: true }));
      const response: any = await getListAttributes(
        companyId ?? '',
        filterAttrs.page,
        pageSize,
        filterAttrs.searchValue
      );
      const rows = response?.data?.data?.attributes ?? [];
      if (isAppend) {
        setListAttributes((prev: any) => {
          const seen = new Set(prev.map((x: any) => String(x.id)));
          const merged = [...prev];
          for (const r of rows) {
            const rid = String(r.id);
            if (!seen.has(rid)) {
              seen.add(rid);
              merged.push(r);
            }
          }
          return merged;
        });
      } else {
        setListAttributes(rows);
      }
      setFilterAttributes({
        page: filterAttrs.page + 1,
        searchValue: filterAttrs.searchValue,
      });
      setHasMore(prev => ({
        ...prev,
        attributes: rows.length >= pageSize,
      }));
    } catch (error) {
      hasLoadedPageAttributesRef.current.delete(filterAttrs.page);
      console.error('Error loading attributes:', error);
    } finally {
      setLoading(prev => ({ ...prev, attributes: false }));
    }
  };

  const bootstrapAttributes = async () => {
    hasLoadedPageAttributesRef.current.clear();
    setFilterAttributes({ page: 1, searchValue: '' });
    await fetchAttributesList({ page: 1, searchValue: '' }, PAGE_SIZE, false);
    hasLoadedPageAttributesRef.current.add(1);
  };

  const handleSearchAttributes = async (value: string) => {
    hasLoadedPageAttributesRef.current.clear();
    setFilterAttributes({ page: 1, searchValue: value });
    await fetchAttributesList({ page: 1, searchValue: value }, PAGE_SIZE, false);
    hasLoadedPageAttributesRef.current.add(1);
  };

  const onLoadMoreAttributes = async () => {
    if (hasLoadedPageAttributesRef.current.has(filterAttributes.page)) return;
    hasLoadedPageAttributesRef.current.add(filterAttributes.page);
    await fetchAttributesList(filterAttributes, PAGE_SIZE, true);
  };

  const onLoadMoreUOM = async () => {
    if (hasLoadedPageUOMRef.current.has(filterUOM.page)) return;
    hasLoadedPageUOMRef.current.add(filterUOM.page);
    setLoading(prev => ({ ...prev, uom: true }));
    await fetchUOMList(filterUOM, PAGE_SIZE, true);
  };
  const onLoadMoreGroup = async () => {
    if (hasLoadedPageGroupRef.current.has(filterGroup.page)) return;
    hasLoadedPageGroupRef.current.add(filterGroup.page);
    setLoading(prev => ({ ...prev, group: true }));
    await fetchGroup(filterGroup, PAGE_SIZE, true);
  };

  const handleSearchGroup = async (value: string) => {
    setLoading(prev => ({ ...prev, group: true }));
    try {
      const response: any = await getGroupProduct(
        localStorage.getItem('companyId'),
        1,
        PAGE_SIZE,
        value
      );
      setGroupItems(response?.data?.productGroupWithCompany);
      setFilterGroup((prev: any) => ({
        ...prev,
        page: filterGroup.page + 1,
        searchValue: filterGroup.searchValue,
      }));
    } catch (err) {
      console.error(err);
      hasLoadedPageRef.current.delete(filter.pageProduct);
    } finally {
      setLoading(prev => ({ ...prev, group: false }));
      setHasMore(prev => ({ ...prev, group: true }));
    }
  };

  const handleSearchUOM = async (value: string) => {
    setLoading(prev => ({ ...prev, uom: true }));
    try {
      const response: any = await getListUOM(localStorage.getItem('userId'), 1, PAGE_SIZE, value);
      setListUOM(response?.data?.data);
      setFilterUOM((prev: any) => ({
        ...prev,
        page: filterUOM.page + 1,
        searchValue: filterUOM.searchValue,
      }));
    } catch (err) {
      console.error(err);
      hasLoadedPageUOMRef.current.delete(filter.pageProduct);
    } finally {
      setLoading(prev => ({ ...prev, uom: false }));
      setHasMore(prev => ({ ...prev, uom: true }));
    }
  };

  const handleSearchUOMConfiguration = async (value: string) => {
    // setListUOMConfiguration(response?.data?.data);
  };

  const onLoadMoreStock = async () => {
    if (hasLoadedPageRef.current.has(filterStock.page)) return;
    hasLoadedPageRef.current.add(filterStock.page);
    setLoading(prev => ({ ...prev, product: true }));
    await fetchStockHoldings(filterStock, PAGE_SIZE, true);
  };

  const fetchStockHoldings = async (
    filterStock: any,
    PAGE_SIZE: number,
    isAppend: boolean = true
  ) => {
    try {
      setLoading(prev => ({ ...prev, product: true }));
      const response: any = await getSockHolding(
        filterStock.page,
        PAGE_SIZE,
        filterStock.searchValue,
        localStorage.getItem('locationId'),
        id !== 'new' ? id : undefined
      );
      if (isAppend) {
        setListStock((prev: any) => [...prev, ...response?.data?.dataStock]);
      } else {
        setListStock(response?.data?.dataStock);
      }
      setFilterStock((prev: any) => ({
        ...prev,
        page: filterStock.page + 1,
        searchValue: filterStock.searchValue,
      }));
      setHasMore(prev => ({
        ...prev,
        product: response?.data?.dataStock.length >= PAGE_SIZE,
      }));
    } catch (error) {
      hasLoadedPageRef.current.delete(filterStock.page);
    } finally {
      setLoading(prev => ({ ...prev, product: false }));
    }
  };

  const fetchGroup = async (filterGroup: any, PAGE_SIZE: number, isAppend: boolean = true) => {
    try {
      setLoading(prev => ({ ...prev, group: true }));
      const response: any = await getGroupProduct(
        localStorage.getItem('companyId'),
        filterGroup.page,
        PAGE_SIZE,
        filterGroup.searchValue
      );
      if (isAppend) {
        setGroupItems((prev: any) => [...prev, ...response?.data?.productGroupWithCompany]);
      } else {
        setGroupItems(response?.data?.productGroupWithCompany);
      }
      setFilterStock((prev: any) => ({
        ...prev,
        page: filterGroup.page + 1,
        searchValue: filterGroup.searchValue,
      }));
      setHasMore(prev => ({
        ...prev,
        group: response?.data?.productGroupWithCompany.length >= PAGE_SIZE,
      }));
    } catch (error) {
      hasLoadedPageRef.current.delete(filterGroup.page);
    } finally {
      setLoading(prev => ({ ...prev, group: false }));
    }
  };

  const fetchUOMList = async (filterUOM: any, PAGE_SIZE: number, isAppend: boolean = true) => {
    try {
      setLoading(prev => ({ ...prev, group: true }));
      const response: any = await getListUOM(
        localStorage.getItem('userId'),
        filterUOM.page,
        PAGE_SIZE,
        filterUOM.searchValue
      );
      if (isAppend) {
        setListUOM((prev: any) => [...prev, ...response?.data?.data]);
      } else {
        setListUOM(response?.data?.data);
      }
      setFilterUOM((prev: any) => ({
        ...prev,
        page: filterUOM.page + 1,
        searchValue: filterUOM.searchValue,
      }));
      setHasMore(prev => ({
        ...prev,
        uom: response?.data?.data.length >= PAGE_SIZE,
      }));
    } catch (error) {
      hasLoadedPageUOMRef.current.delete(filterUOM.page);
    } finally {
      setLoading(prev => ({ ...prev, uom: false }));
    }
  };

  const handleAssetCategorySelect = (category: any) => {
    setFormValues((prev: any) => ({
      ...prev,
      assetCategoryId: category.id,
    }));
    setAssetCategory(category?.categoryName);
    setIsAssetCategoryDropdownOpen(false);
  };

  const fetchUOMAltList = async (baseUOMId: string) => {
    setLoading(prev => ({ ...prev, uom: true }));
    try {
      const res = await getListUOMAlt(baseUOMId, 0, 0, '');
      setListUOMAlt(res?.data?.data);
    } catch (err: any) {
      console.log('🚀 ~ fetchGroupItems ~ err:', err);
    } finally {
      setLoading(prev => ({ ...prev, uom: false }));
      setHasMore(prev => ({ ...prev, uom: false }));
    }
  };

  const handleStartDate = (value: any) => {
    setStartDate(value);
    setFormValues({ ...formValues, onSaleDate: value });
  };
  const handleContractExpiration = (value: any) => {
    setContractExpiration(value);
    setFormValues({ ...formValues, offSaleDate: value });
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'displayName') {
      setIsDisplayNameCustomized(true);
      setFormValues((prev: any) => ({ ...prev, [name]: value }));
      return;
    }

    if (name === 'productName') {
      setFormValues((prev: any) => ({
        ...prev,
        productName: value,
        ...(id === 'new' && !isDisplayNameCustomized ? { displayName: value } : {}),
      }));
      return;
    }

    setFormValues((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleGroupItemsSelect = (group: { id: string; groupName: string }) => {
    setFormValues((prev: any) => ({ ...prev, productGroupId: group.id }));
    setSearchGroupItems(group.groupName);
    setIsGroupItemsDropdownOpen(false);
  };
  const handleCourseSelect = (course: { id: string; name: string } | null) => {
    setFormValues((prev: any) => ({ ...prev, courseId: course?.id ?? null }));
    setSearchCourse(course?.name ?? '');
    setIsCourseDropdownOpen(false);
  };
  const handleDelete = (index: number) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows);
    setSearchTaxMap((prevMap: any) => {
      const newMap: any = {};
      Object.entries(prevMap).forEach(([key, value]) => {
        const k = Number(key);
        if (k < index) {
          newMap[k] = value;
        } else if (k > index) {
          newMap[k - 1] = value;
        }
      });
      return newMap;
    });
  };
  const handleDeleteRecipe = (index: number) => {
    setFormValues((prev: any) => ({
      ...prev,
      recipeData: prev?.recipeData?.filter((_: any, i: number) => i !== index) ?? [],
    }));
  };

  const handleUOMSelect = (uomBase: { id: string; uomName: string }) => {
    setFormValues((prev: any) => {
      if (prev.baseUOMId !== uomBase.id) {
        prev.alternativeIds = [];
      }
      return { ...prev, baseUOMId: uomBase.id };
    });
    setSearchUOM(uomBase.uomName);
    setIsUOMDropdownOpen(false);
    fetchUOMAltList(uomBase.id);
  };

  const handleUOMSelectAlt = (UOM: any) => {
    setAlternativeId(prev => ({
      allowOrdering: prev.allowOrdering,
      allowRecipe: prev.allowRecipe,
      UOMId: UOM.id,
      uomName: UOM.uomName,
    }));
    setSearchUOMAlt(UOM.uomName);
    setIsUOMAltDropdownOpen(false);
  };

  const handleCancelAlternativeUOM = () => {
    setShowAlt(false);
    setErrors({});
    setAlternativeId({
      allowOrdering: false,
      allowRecipe: false,
      UOMId: '',
      uomName: '',
    });
    setSearchUOMAlt('');
  };

  const filesDialogRef = useRef<HTMLDivElement>(null);
  const altDialogRef = useRef<HTMLDivElement>(null);
  const deleteDialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(showPopupImage, filesDialogRef, () => setShowPopupImage(false));
  useDialogFocus(showAlt, altDialogRef, handleCancelAlternativeUOM);
  useDialogFocus(showDelete, deleteDialogRef, () => {
    setShowDelete(false);
    setIdSelect('');
  });

  const handleAddAlternativeUOM = () => {
    setShowAlt(false);
    setAlternativeId({
      allowOrdering: false,
      allowRecipe: false,
      UOMId: '',
      uomName: '',
    });
    setSearchUOMAlt('');
    if (alternativeId.UOMId !== '') {
      setFormValues((prev: any) => {
        const existingIndex = prev.alternativeIds.findIndex((item: any) => {
          return item.UOMId === alternativeId.UOMId;
        });
        if (existingIndex !== -1) {
          const updatedAlternativeIds = [...prev.alternativeIds];
          updatedAlternativeIds[existingIndex] = {
            ...updatedAlternativeIds[existingIndex],
            allowOrdering: alternativeId.allowOrdering,
            allowRecipe: alternativeId.allowRecipe,
            uomName: alternativeId.uomName,
          };
          return {
            ...prev,
            alternativeIds: updatedAlternativeIds,
          };
        }
        return {
          ...prev,
          alternativeIds: [...prev.alternativeIds, alternativeId],
        };
      });
    }
  };
  const handleEditAlt = (value: any) => {
    setShowAlt(true);
    setAlternativeId({
      allowOrdering: value.allowOrdering,
      allowRecipe: value.allowRecipe,
      UOMId: value.UOMId,
      uomName: value.uomName,
    });
    setSearchUOMAlt(listUOMAlt.find((v: any) => v.id === value.UOMId)?.uomName ?? value.uomName);
  };

  const handleExpenseSelect = (value: any) => {
    setFormValues((prev: any) => ({ ...prev, chartsOfAccountsExpenseId: value.id }));
    setSearchExpense(String(value.accountCode + ' - ' + value.accountName));
    setIsExpenseDropdownOpen(false);
    setErrors((prev: any) => ({ ...prev, chartsOfAccountsExpenseId: null }));
  };

  const handleIncomeSelect = (value: any) => {
    setFormValues((prev: any) => ({ ...prev, chartsOfAccountsIncomeId: value.id }));
    setSearchIncome(String(value.accountCode + ' - ' + value.accountName));
    setIsIncomeDropdownOpen(false);
    setErrors((prev: any) => ({ ...prev, chartsOfAccountsIncomeId: null }));
  };

  const formatDate = (dateString: any) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };


  const handleEditRecipe = (index: number) => {
    const item = formValues.recipeData[index];
    const toNullableNumber = (value: any) => {
      if (value === null || value === undefined || value === '') return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    };

    // Set lại giá trị vào form
    setFormValues((prev: any) => ({
      ...prev,
      quantity: toNullableNumber(item.quantity),
      calories: toNullableNumber(item.calories),
    }));

    // Set lại dropdown values
    setSearchStockItems(item.productName);
    setSearchIdStockItems(item.stockId);
    setSearchUOMRecipt(item.uomName);
    setSearchIdUOMRecpit(item.uomId);

    // Đánh dấu đang edit item nào
    setEditingIndex(index);
  };

  // Cập nhật hàm handleAddStockToRecipes
  const handleAddStockToRecipes = () => {
    const itemSelect = listStock.find((v: any) => v.id === searchIdStockItems);
    const parseNullableNumber = (value: any) => {
      if (value === null || value === undefined || value === '') return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    };
    console.log('🚀 ~ handleAddStockToRecipes ~ itemSelect:', itemSelect);
    console.log('🚀 ~ handleAddStockToRecipes ~ searchIdUOMRecpit:', searchIdUOMRecpit);

    if (!itemSelect) return;

    if (editingIndex !== null) {
      // Đang ở chế độ edit - cập nhật item
      setFormValues((prev: any) => {
        const recipeList = [...prev.recipeData];
        recipeList[editingIndex] = {
          ...recipeList[editingIndex],
          stockId: itemSelect.id,
          uomId: searchIdUOMRecpit,
          quantity: parseNullableNumber(formValues?.quantity),
          calories: parseNullableNumber(formValues?.calories),
          productName: itemSelect.productName,
          uomName: searchUOMRecipt,
        };

        return {
          ...prev,
          recipeData: recipeList,
        };
      });

      // Reset edit mode
      setEditingIndex(null);
    } else {
      // Thêm mới như code cũ
      setFormValues((prev: any) => {
        const recipeList = prev?.recipeData || [];
        return {
          ...prev,
          recipeData: [
            ...recipeList,
            {
              stockId: itemSelect.id,
              uomId: searchIdUOMRecpit,
              quantity: parseNullableNumber(formValues?.quantity),
              calories: parseNullableNumber(formValues?.calories),
              isActive: true,
              productName: itemSelect.productName,
              uomName: searchUOMRecipt,
              createdAt: new Date(),
            },
          ],
        };
      });
    }

    // Reset form
    setSearchStockItems('');
    setIsStockItemsDropdownOpen(false);
    setSearchIdStockItems('');
    setSearchUOMRecipt('');
    setSearchIdUOMRecpit('');
    setIsUOMDropdownReciptOpen(false);
    setFormValues((prev: any) => ({ ...prev, quantity: '', calories: '' }));
  };

  // Hàm cancel edit
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setSearchStockItems('');
    setSearchIdStockItems('');
    setSearchUOMRecipt('');
    setSearchIdUOMRecpit('');
    setFormValues((prev: any) => ({ ...prev, quantity: '', calories: '' }));
  };

  /**
   * Allergen pills cycle: none -> contains -> may contain -> none.
   * Assigning allergens is optional; clearing every pill is valid and means
   * "no information recorded", not "declared allergen free".
   */
  const handleToggleAllergen = (allergenId: string) => {
    setSelectedAllergens((prev) => {
      const next = { ...prev };
      const current = next[allergenId];
      if (current === undefined) {
        next[allergenId] = ALLERGEN_LEVEL.CONTAINS;
      } else if (current === ALLERGEN_LEVEL.CONTAINS) {
        next[allergenId] = ALLERGEN_LEVEL.MAY_CONTAIN;
      } else {
        delete next[allergenId];
      }
      return next;
    });
  };

  const handleRemoveAttributes = (v: any) => {
    setListAttributesSelect((prev: any) => {
      return prev.map((val: any, idx: number) => ({
        ...val,
        isSelect: v.id === val.id ? false : val.isSelect,
      }));
    });
  };

  const handleGenerateBarcode = () => {
    setBarcodes('');
    setListBarcode((prev: any) => [
      ...prev,
      barcodes?.trim()
        ? barcodes
        : Array.from({ length: 11 }, () => Math.floor(Math.random() * 10)).join(''),
    ]);
  };

  const handleDeleteBarcode = (v: any) => {
    setListBarcode((prev: any) => prev.filter((val: any) => val !== v));
  };

  const handleAttributesItemsSelect = (v: any) => {
    const exists = listAttributesSelect.find((val: any) => val.id === v.id);
    if (exists) {
      setListAttributesSelect((prev: any) => {
        return prev.map((val: any, idx: number) => ({
          ...val,
          isSelect: v.id === val.id ? true : val.isSelect,
        }));
      });
    } else {
      setListAttributesSelect((prev: any) => [...prev, { ...v, isSelect: true }]);
    }
    // Close the dropdown after a pick so the selected attribute is visible
    // (matches the stock-items picker, which closes on select).
    setIsAttributesItemsDropdownOpen(false);
  };
  const handleStockItemsSelect = (value: any) => {
    console.log('🚀 ~ handleStockItemsSelect ~ value:', value);
    setListUOMConfiguration(value.productUOMs);
    setSearchStockItems(value?.productName);
    setSearchIdStockItems(value?.id);
    setIsStockItemsDropdownOpen(false);
    setSearchUOMRecipt('');
  };

  const handleUOMReciptSelect = (value: any) => {
    console.log('🚀 ~ handleUOMReciptSelect ~ value:', value);
    setSearchUOMRecipt(value?.uom?.uomName);
    setSearchIdUOMRecpit(value?.UOMId);
    setIsUOMDropdownReciptOpen(false);
  };
  const handleTypeSelect = (type: { name: string; value: number }) => {
    setFormValues((prev: any) => ({
      ...prev,
      type: type.value,
      deprecationYears: type.name !== 'Asset' ? null : prev.deprecationYears,
      assetCategoryId: type.name !== 'Asset' ? null : prev.assetCategoryId,
    }));
    setAssetCategory((prev: any) => (type.name !== 'Asset' ? '' : prev));
    setSearchType(type.name);
    setIsTypeDropdownOpen(false);
  };
  const handleSelectType = () => {
    const newRow = {
      price: '',
      cost: '',
      validFrom: new Date(),
      validTo: getDateAfterMonths(24),
      isActive: true,
      // Default to Regular (1) — matches the DB column default. Admin
      // can switch via the inline dropdown in the prices table. The
      // POS reads price_type via /sync/product-prices/ to pick the
      // sale-time entry UI.
      priceType: PriceType.Regular,
    };

    if (rows.length < 1) {
      setRows(prevRows => [...prevRows, newRow]);
    } else {
      setRows(prevRows => {
        const last = prevRows[prevRows.length - 1];
        const lastToNorm = normalizePriceDay(last?.validTo);
        let nextFrom = new Date();
        if (lastToNorm) {
          nextFrom = new Date(lastToNorm);
          nextFrom.setDate(nextFrom.getDate() + 1);
        }
        const nextTo = getDateAfterMonthsFrom(nextFrom, 24);
        const newRowWithUpdatedDates = {
          price: '',
          cost: '',
          validFrom: nextFrom,
          validTo: nextTo,
          isActive: true,
          priceType: PriceType.Regular,
        };
        return [...prevRows, newRowWithUpdatedDates];
      });
    }
  };

  const checkIfAllFieldsAreFilled = (rows: any) => {
    const missing: string[] = [];
    rows.forEach((row: any, index: number) => {
      if (row.price === '' || row.price == null) {
        missing.push(`Price row ${index + 1}: enter a price.`);
      }
      if (row.cost === '' || row.cost == null) {
        missing.push(`Price row ${index + 1}: enter a cost.`);
      }
      if (!row.validFrom) {
        missing.push(`Price row ${index + 1}: choose a start date.`);
      }
      // valid_to is intentionally optional: a null end date means an open-ended
      // current price — what the "change price" flow creates and the normal
      // state of the latest price. The BE DTO is @IsOptional + nullable, and the
      // "valid from < valid to" checks only run when an end date is set, so a
      // missing end date must not block saving.
    });

    if (missing.length > 0) {
      return { isValid: false, errors: missing };
    }
    return { isValid: true, errors: [] };
  };

  const handleToggle = (index: number) => {
    setRows(prevRows => {
      const updatedRows = [...prevRows];
      const row = { ...updatedRows[index] };
      row.isActive = !row.isActive;
      updatedRows[index] = row;
      return updatedRows;
    });
  };

  const handleToggleRecipe = (index: number) => {
    setFormValues((prev: any) => {
      const updatedRows = [...(prev?.recipeData ?? [])];
      const row = { ...updatedRows[index] };
      row.isActive = !row.isActive;
      updatedRows[index] = row;
      return {
        ...prev,
        recipeData: updatedRows,
      };
    });
  };

  const handleTaxDropdownToggle = (index: number) => {
    setOpenDropdownIndexes(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };
  const handleOpenPopup = () => {
    setShowPopup(true);
  };
  const handleInputServiceChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: string
  ) => {
    const value = e.target.value;
    setRows(prevRows => {
      const updatedRows = [...prevRows];
      const row = { ...updatedRows[index] };
      row[field] = value;
      updatedRows[index] = row;
      return updatedRows;
    });
  };

  const handleTaxSelect = (tax: any, index: number) => {
    const updatedRows = [...rows];
    const rate = tax.primaryRate ?? tax.primary_rate ?? '';
    updatedRows[index] = {
      ...updatedRows[index],
      tax: rate,
      taxGroupId: tax.id,
    };
    setRows(updatedRows);
    setSearchTaxMap((prev: any) => ({ ...prev, [index]: taxGroupDisplayName(tax) }));
    setOpenDropdownIndexes(prev => ({ ...prev, [index]: false }));
  };

  const getFilteredTaxList = (index: number) => {
    const query = (searchTaxMap[index] ?? '').toString().trim().toLowerCase();
    if (!query) return listTax;
    return listTax.filter((tax: any) => {
      const name = String(tax.groupName ?? '').toLowerCase();
      return name.includes(query);
    });
  };

  const handleTaxInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key !== 'Enter' && e.key !== 'Tab') return;
    const close = () => setOpenDropdownIndexes(prev => ({ ...prev, [index]: false }));
    // Ctrl/Cmd+Enter is the form's save: never swallow it.
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) return close();
    const isOpen = !!openDropdownIndexes[index];
    const filtered = getFilteredTaxList(index);
    const query = (searchTaxMap[index] ?? '').toString().trim();
    const exact = filtered.find(
      (t: any) => String(t.groupName ?? '').toLowerCase() === query.toLowerCase()
    );
    // Typed text picks the exact or first match; an empty box picks nothing.
    const match = query ? exact ?? filtered[0] : undefined;
    if (e.key === 'Tab') {
      if (match) handleTaxSelect(match, index);
      close();
      return;
    }
    // Enter in an open list stays here; on a closed field it reaches the form unless it picks a new rate.
    if (isOpen || (match && !exact)) e.preventDefault();
    if (match) handleTaxSelect(match, index);
    else close();
  };

  const handleSelectValidFrom = (date: any, index: any) => {
    if (!date) return;
    const idx = Number(index);

    // ✅ Dùng trực tiếp, chỉ normalize để so sánh
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selectedNormalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (selectedNormalized < today) {
      setShowPopup(true);
      setErrors((prev: any) => ({
        ...prev,
        missingFields: language?.['Cannot select past date'] || 'Cannot select past date',
      }));
      return;
    }

    setRows((prevRows: any[]) => {
      const newRows = [...prevRows];
      const currentRow = newRows[idx];
      if (!currentRow) return prevRows;

      // Auto update previous row's Valid To = (current Valid From - 1 day)
      if (idx > 0 && newRows[idx - 1]?.validFrom) {
        const prevFrom = newRows[idx - 1].validFrom;
        const prevFromNormalized = new Date(
          new Date(prevFrom).getFullYear(),
          new Date(prevFrom).getMonth(),
          new Date(prevFrom).getDate()
        );

        if (selectedNormalized > prevFromNormalized) {
          const autoValidTo = new Date(selectedNormalized);
          autoValidTo.setDate(autoValidTo.getDate() - 1);
          newRows[idx - 1] = {
            ...newRows[idx - 1],
            validTo: autoValidTo,
          };
        } else {
          setShowPopup(true);
          setErrors((prev: any) => ({
            ...prev,
            missingFields:
              language?.['Date must be after previous period start'] ||
              'Date must be after previous period start',
          }));
          return prevRows;
        }
      }

      // Validate current row Valid From < Valid To
      if (newRows[idx].validTo) {
        const validToNormalized = new Date(
          new Date(newRows[idx].validTo).getFullYear(),
          new Date(newRows[idx].validTo).getMonth(),
          new Date(newRows[idx].validTo).getDate()
        );

        if (selectedNormalized >= validToNormalized) {
          setShowPopup(true);
          setErrors((prev: any) => ({
            ...prev,
            missingFields:
              language?.['Valid from must be before valid to'] || 'Valid from must be before valid to',
          }));
          return prevRows;
        }
      }

      newRows[idx] = {
        ...newRows[idx],
        validFrom: date,
      };
      return newRows;
    });
  };

  const handleSelectValidTo = (date: any, index: any) => {
    if (!date) return;

    const newRows = [...rows];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selectedNormalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (selectedNormalized < today) {
      setShowPopup(true);
      setErrors((prev: any) => ({
        ...prev,
        missingFields: language?.['Cannot select past date'] || 'Cannot select past date',
      }));
      return;
    }

    if (newRows[index].validFrom) {
      const fromNormalized = new Date(
        new Date(newRows[index].validFrom).getFullYear(),
        new Date(newRows[index].validFrom).getMonth(),
        new Date(newRows[index].validFrom).getDate()
      );

      if (selectedNormalized <= fromNormalized) {
        setShowPopup(true);
        setErrors((prev: any) => ({
          ...prev,
          missingFields:
            language?.['Valid to must be after valid from'] || 'Valid to must be after valid from',
        }));
        return;
      }
    }

    if (index < rows.length - 1) {
      if (!newRows[index + 1].validFrom) {
        const autoNextValidFrom = new Date(selectedNormalized);
        autoNextValidFrom.setDate(autoNextValidFrom.getDate() + 1);
        newRows[index + 1].validFrom = autoNextValidFrom;
      } else {
        const nextNormalized = new Date(
          new Date(newRows[index + 1].validFrom).getFullYear(),
          new Date(newRows[index + 1].validFrom).getMonth(),
          new Date(newRows[index + 1].validFrom).getDate()
        );

        if (selectedNormalized >= nextNormalized) {
          setShowPopup(true);
          setErrors((prev: any) => ({
            ...prev,
            missingFields:
              language?.['Period overlaps with next product'] || 'Period overlaps with next product',
          }));
          return;
        }
      }
    }

    newRows[index].validTo = date;
    setRows(newRows);
  };
  /**
   * Step-1 required-field checks (the "Required to Sell" set). Shared by the
   * footer Next button and handleSubmit so a user can't reach Step 2 — or
   * save — with the basics missing.
   *
   * Expense Code / Income Code are intentionally optional. Items can be
   * saved without a charts-of-accounts mapping (e.g. when the COA isn't
   * configured yet, or for sale-only items the finance team will tag
   * later). Backend mirrors this — see CreateProductDto where both
   * chartsOfAccountsExpenseId and chartsOfAccountsIncomeId are
   * @IsOptional + nullable. The payload normalisation in handleSubmit
   * converts the empty-string default back to null on submit.
   */
  const collectStep1Errors = () => {
    const newErrors: any = validateAgainstLimits(formValues, limits, language, ['sku', 'productName', 'displayName']);
    if (!formValues.productGroupId) {
      newErrors.productGroupId = 'Select an item group.';
    }
    if (!formValues.productName) {
      newErrors.productName = 'Enter an item name.';
    }
    if (!formValues.type) {
      newErrors.type = 'Select an item type.';
    }
    if (!formValues.baseUOMId && searchType !== 'Sale Item') {
      newErrors.baseUOMId = 'Select a base unit.';
    }
    return newErrors;
  };
  const handleSubmit = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const newErrors: any = collectStep1Errors();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // The error messages render under the Step-1 fields; without this a
      // failed save from Step 2 would look like a dead Save button.
      setStep(1);
      return;
    }

    let workingPriceRows = rows;
    if (searchType !== 'Stock Item' && rows.length >= 2) {
      const { keptRows, removedCount } = removeOverlappingPriceRows(rows);
      if (removedCount > 0) {
        workingPriceRows = keptRows;
        setRows(keptRows);
        showToast({
          message:
            language?.['Removed overlapping price rows'] ||
            `Removed ${removedCount} overlapping price row(s).`,
          type: 'warning',
          duration: 10000,
        });
      }
    }

    const missing: any = checkIfAllFieldsAreFilled(workingPriceRows);
    // if (searchType !== "Asset" && listAttributes.filter((v: any) => v.isSelect).length === 0) {
    //   setShowPopup(true);
    //   setErrors((prev: any) => ({
    //     ...prev,
    //     missingFields: "Please select at least one attribute"
    //   }));
    //   return;
    // }
    if (workingPriceRows.length === 0 && searchType !== 'Stock Item') {
      setShowPopup(true);
      setErrors((prev: any) => ({
        ...prev,
        missingFields: 'Please fill in all required information related to prices',
      }));
      return;
    }
    if (!missing.isValid && searchType !== 'Stock Item') {
      setShowPopup(true);
      setErrors((prev: any) => ({
        ...prev,
        missingFields: missing.errors.join(', '),
      }));
      return;
    }
    const formData = new FormData();
    formData.append('image', fileUpload);
    webLoadingTrue();
    // allergens is stripped: the detail response's read shape rides in
    // formValues, and only the deliberate assignment below may send it.
    const { recipe, calories, quantity, allergens: detailAllergens, ...restFormValues } = formValues;
    const payload = {
      ...restFormValues,
      courseId: formValues.courseId || null,
      // fetchData defaults the IDs to `''` (empty string) when the joined
      // productExpenseCode / productIncomeCode came back null, and the
      // dropdowns leave the IDs as `''` when nothing is selected. The
      // BE DTO is `string | null` with @IsOptional + @IsUUID — sending
      // `''` would fail the UUID check (400). Coerce empty/falsy to null
      // here, same pattern as `courseId` above, so the BE sees a clean
      // optional field.
      chartsOfAccountsExpenseId: formValues.chartsOfAccountsExpenseId || null,
      chartsOfAccountsIncomeId: formValues.chartsOfAccountsIncomeId || null,
      maxDiscount: formValues.maxDiscount ? Number(formValues.maxDiscount) : 0,
      userId,
      companyId,
      onSaleDate: formValues?.onSaleDate ? formValues?.onSaleDate : startDate,
      offSaleDate: formValues?.offSaleDate ? formValues?.offSaleDate : contractExpiration,
      productPrice: workingPriceRows,
      barcodes: listBarcode,
    };
    if (searchType === 'Asset') {
      payload.deprecationYears = Number(formValues?.deprecationYears);
    } else {
      payload.attributeIds =
        listAttributesSelect.filter((v: any) => v.isSelect).map((val: any) => val.id) || [];
      // Optional: an empty array clears assignments; it is not a claim that
      // the product is allergen free. Sent only when the card was shown
      // (Stock Item hides it, so its saves must not touch assignments) and,
      // on edit, only when the prefill provably ran — otherwise the key is
      // omitted and the API leaves existing assignments untouched.
      if (
        searchType !== 'Stock Item' &&
        (id === 'new' || allergensPrefilledRef.current)
      ) {
        const allergenAssignments: ProductAllergen[] = Object.entries(
          selectedAllergens
        ).map(([allergenId, level]) => ({ allergenId, level }));
        payload.allergens = allergenAssignments;
      }
    }
    if (searchType === 'Stock Item') {
      // Stock items are purchased: keep the purchase tax. The API falls back
      // to the country default when it is empty.
      payload.productConfigurationData = {
        isCountable: formValues.productConfigurationData?.isCountable !== false,
        ...(formValues.productConfigurationData?.purchaseTaxId
          ? { purchaseTaxId: formValues.productConfigurationData.purchaseTaxId }
          : {}),
      };
      payload.maxDiscount = null;
    }
    if (searchType === 'Sale Item') {
      payload.productConfigurationData = {
        isCountable: formValues.productConfigurationData?.isCountable !== false,
        purchaseTaxId: formValues.productConfigurationData?.purchaseTaxId ?? null,
        print_group_ids: formValues.productConfigurationData?.print_group_ids ?? [],
      };
      payload.baseUOMId = null;
    }
    formData.append('payload', JSON.stringify(payload));
    if (id === 'new') {
      try {
        const response = await createProduct(formData);
        webNavigate('/' + preLanguage + '/items' + '?tab=Items');
        webLoadingFalse();
        showToast({
          message: 'Items Created !',
          type: 'success',
          duration: 20000,
        });
      } catch (error: any) {
        webLoadingFalse();
        showApiError(error, {
          language,
          titleFallback: "Couldn't create the item",
          setFieldErrors: fe => setErrors((prev: any) => ({ ...prev, ...fe })),
          fieldSteps: ITEM_FIELD_STEPS,
          onStep: setStep,
        });
      }
    } else {
      try {
        const response = await updateProduct(id, formData);
        webNavigate('/' + preLanguage + '/items' + '?tab=Items');
        webLoadingFalse();
        showToast({
          message: language?.['Items Updated !'] ?? 'Items Updated !',
          type: 'success',
          duration: 20000,
        });
      } catch (error: any) {
        webLoadingFalse();
        showApiError(error, {
          language,
          titleFallback: "Couldn't update the item",
          setFieldErrors: fe => setErrors((prev: any) => ({ ...prev, ...fe })),
          fieldSteps: ITEM_FIELD_STEPS,
          onStep: setStep,
        });
      }
    }
  };
  const handleRemoveFile = () => {
    setFileName('');
    setFileUpload(null);
    setPreviewImg(null);
    setFormValues((prev: any) => ({ ...prev, image: null }));
    let inputHoolder = document.getElementById('fileInput') as HTMLInputElement;
    inputHoolder.value = '';
  };
  const handleBlurServiceChange = (
    e: React.FocusEvent<HTMLInputElement, Element>,
    index: number,
    field: string
  ) => {
    setRows(prevRows => {
      const updatedRows = [...prevRows];
      const row = { ...updatedRows[index] };
      row[field] = Number(row[field]).toFixed(2);
      updatedRows[index] = row;
      return updatedRows;
    });
  };

  const CustomCalendarContainer = ({ children, className }: any) => {
    return (
      <div
        className={className}
        style={{
          position: 'absolute',
          marginTop: '-305px',
          marginLeft: '-175px',
          ...(isMobile
            ? {
              padding: '12px',
              border: '1px solid #DBE4E4',
            }
            : {}),
        }}
      >
        {children}
      </div>
    );
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const processFile = (file: File) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/heic', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setShowPopup(true);
      setErrors((prev: any) => ({
        ...prev,
        missingFields: 'Only .jpeg, .jpg, .heic, .png, .webp files are allowed',
      }));
      return;
    }

    const MAX_FILE_BYTES = 50 * 1024;
    const OUT_SIZE = 200;
    const MIN_SIZE = 80;

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = async () => {
      const size = Math.min(img.width, img.height);
      const startX = (img.width - size) / 2;
      const startY = (img.height - size) / 2;

      const toBlobAsync = (canvas: HTMLCanvasElement, quality: number) =>
        new Promise<Blob | null>(resolve =>
          canvas.toBlob(b => resolve(b), 'image/jpeg', quality)
        );

      let canvas = document.createElement('canvas');
      canvas.width = OUT_SIZE;
      canvas.height = OUT_SIZE;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, startX, startY, size, size, 0, 0, OUT_SIZE, OUT_SIZE);

      let q = 0.92;
      let blob: Blob | null = await toBlobAsync(canvas, q);
      while (blob && blob.size > MAX_FILE_BYTES && q > 0.45) {
        q -= 0.07;
        blob = await toBlobAsync(canvas, q);
      }
      while (blob && blob.size > MAX_FILE_BYTES && canvas.width > MIN_SIZE) {
        const ns = Math.max(MIN_SIZE, Math.floor(canvas.width * 0.85));
        const c2 = document.createElement('canvas');
        c2.width = ns;
        c2.height = ns;
        const c2x = c2.getContext('2d');
        if (!c2x) break;
        c2x.drawImage(canvas, 0, 0, ns, ns);
        canvas = c2;
        q = 0.92;
        blob = await toBlobAsync(canvas, q);
        while (blob && blob.size > MAX_FILE_BYTES && q > 0.45) {
          q -= 0.07;
          blob = await toBlobAsync(canvas, q);
        }
      }

      if (!blob) return;
      const outName = file.name.replace(/\.[^.]+$/i, '.jpg');
      const croppedFile = new File([blob], outName, { type: 'image/jpeg' });
      setFileUpload(croppedFile);
      setPreviewImg(URL.createObjectURL(blob));
      setFileName(file.name);
      setErrors((prev: any) => {
        const next = { ...prev };
        delete next.missingFields;
        return next;
      });
    };
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleSubmitSearchStock = async (value: string) => {
    await fetchStockHoldings({ page: 1, searchValue: value }, PAGE_SIZE, false);
  };

  const togglePrintGroup = (groupId: string) => {
    setFormValues((prev: any) => {
      const cur: string[] = prev.productConfigurationData?.print_group_ids || [];
      const next = cur.includes(groupId) ? cur.filter((x) => x !== groupId) : [...cur, groupId];
      return {
        ...prev,
        productConfigurationData: {
          ...prev.productConfigurationData,
          print_group_ids: next,
        },
      };
    });
  };
  /** purchaseTaxId on API = tax_groups.id (same as price taxGroupId). */
  const handleTaxItemsSelect = (val: any) => {
    setFormValues((prev: any) => ({
      ...prev,
      productConfigurationData: {
        ...prev.productConfigurationData,
        purchaseTaxId: val.id,
      },
    }));
    setSearchTaxItems(taxGroupDisplayName(val) || String(val.groupName ?? ''));
    setIsTaxItemsDropdownOpen(false);
  };

  /**
   * Pre-fill the create form from the country defaults, exactly as if the user
   * had picked each value: Item Type, Base UOM, the Selling-card sales tax,
   * purchase tax and income/expense codes. Every fill goes through the same
   * select handler the dropdown uses, only touches fields the user hasn't set,
   * and re-runs as reference lists load so late-resolving fields still fill.
   * Everything stays visible and editable.
   */
  useEffect(() => {
    if (id !== 'new' || !countryDefaults) return;
    const d = countryDefaults;
    if (d.defaultProductType && !formValues.type && !searchType) {
      const t = listType.find((v: any) => v.value === d.defaultProductType);
      if (t) handleTypeSelect(t);
    }
    if (d.defaultBaseUomId && !formValues.baseUOMId && !searchUOM) {
      handleUOMSelect({ id: d.defaultBaseUomId, uomName: d.defaultBaseUomName ?? '' });
    }
    if (d.defaultTaxGroupId && rows.length > 0 && !rows[0]?.taxGroupId) {
      const tg = (listTax as any[]).find((t: any) => String(t.id) === String(d.defaultTaxGroupId));
      if (tg) handleTaxSelect(tg, 0);
    }
    // Price type / cost on the pristine seeded row (price untyped). The seed
    // hardcodes Regular/0; honor the country's values instead when set.
    if (rows.length > 0 && rows[0]?.price === '' && (d.defaultPriceType || d.defaultCost != null)) {
      setRows((prev: any[]) => {
        if (!prev.length || prev[0]?.price !== '') return prev;
        const r = { ...prev[0] };
        if (d.defaultPriceType && r.priceType === PriceType.Regular) r.priceType = d.defaultPriceType;
        if (d.defaultCost != null && (r.cost === 0 || r.cost === '')) r.cost = Number(d.defaultCost);
        const next = [...prev];
        next[0] = r;
        return next;
      });
    }
    if (
      d.defaultPurchaseTaxGroupId &&
      !formValues.productConfigurationData?.purchaseTaxId &&
      !searchTaxItems
    ) {
      const tg = (listTax as any[]).find(
        (t: any) => String(t.id) === String(d.defaultPurchaseTaxGroupId),
      );
      if (tg) handleTaxItemsSelect(tg);
    }
    if (d.defaultIncomeChartsOfAccountsId && !formValues.chartsOfAccountsIncomeId && !searchIncome) {
      const acc = (listSystem as any[]).find(
        (a: any) => String(a.id) === String(d.defaultIncomeChartsOfAccountsId),
      );
      if (acc) handleIncomeSelect(acc);
    }
    if (d.defaultExpenseChartsOfAccountsId && !formValues.chartsOfAccountsExpenseId && !searchExpense) {
      const acc = (listSystem as any[]).find(
        (a: any) => String(a.id) === String(d.defaultExpenseChartsOfAccountsId),
      );
      if (acc) handleExpenseSelect(acc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, countryDefaults, listTax, listSystem, rows.length]);

  const handleCheckboxAltChange = (option: string) => {
    setAlternativeId((prev: any) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  const handleRadioChange = (value: boolean) => {
    setFormValues((prev: any) => ({
      ...prev,
      productConfigurationData: {
        ...prev.productConfigurationData,
        isCountable: value,
      },
    }));
  };

  /** Step 2 → Step 1; Step 1 → danh sách Items (đồng bộ header + footer Cancel/Back). */
  const handleBackNavigation = () => {
    if (step === 2) {
      setStep(1);
    } else {
      webNavigate('/' + preLanguage + '/items' + '?tab=Items');
    }
  };

  // Space/Enter on a focusable div with role=button (upload label, row delete) acts as a click.
  const onActivateKey = (activate: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      activate();
    }
  };

  // A role=switch div toggles on Space only, like a native checkbox; Enter bubbles to the form.
  const onToggleKey = (toggle: () => void) => (e: React.KeyboardEvent) => {
    if (e.key !== ' ' || e.ctrlKey || e.metaKey || e.altKey) return;
    e.preventDefault();
    if (!e.repeat) toggle();
  };

  const pickerKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    key: string,
    isOpen: boolean,
    setOpen: (open: boolean) => void,
    items: any[],
    pick: (item: any) => void,
    labelOf: (item: any) => string,
    selectedId: unknown,
  ) => {
    const current = pickerHighlight.key === key ? pickerHighlight.index : -1;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) setOpen(true);
      if (!items.length) return;
      const next = e.key === 'ArrowDown' ? Math.min(items.length - 1, current + 1) : Math.max(0, current - 1);
      setPickerHighlight({ key, index: next });
      e.currentTarget.parentElement?.querySelectorAll('[role="option"]')[next]?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
      const norm = (s: unknown) => String(s ?? '').trim().toLowerCase();
      const text = norm(e.currentTarget.value);
      const selected = items.find(item => selectedId != null && String(item.id) === String(selectedId));
      // An open list or text typed over the selection keeps Enter here; only a closed, untouched list leaves it to the form.
      const typed = text !== norm(selected ? labelOf(selected) : '');
      if (!isOpen && !typed) return;
      e.preventDefault();
      const match = isOpen && items[current] ? items[current] : typed ? items.find(item => norm(labelOf(item)) === text) : undefined;
      if (match) pick(match);
      else setOpen(!isOpen);
      setPickerHighlight({ key: '', index: -1 });
    } else if (e.key === 'Enter' && isOpen) {
      // Ctrl/Cmd+Enter saves the form: close the list so it does not cover the result.
      setOpen(false);
      setPickerHighlight({ key: '', index: -1 });
    } else if ((e.key === 'Escape' || e.key === 'Tab') && isOpen) {
      if (e.key === 'Escape') e.preventDefault();
      setOpen(false);
      setPickerHighlight({ key: '', index: -1 });
    }
  };

  /** The purchase-tax picker: Step 2 for sale/all items, Step 1 for stock items. */
  const renderPurchaseTaxPicker = () => (
                    <div className="space-y-2" >
                      <div className="flex items-center justify-between">
                        <p
                          className="text-[14px] font-inter leading-[20px] font-medium"
                          style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            lineHeight: '28px',
                            color: '#4A5353',
                          }}
                        >
                          {language && language?.['Select purchase tax rate']
                            ? language?.['Select purchase tax rate']
                            : 'Select purchase tax rate'}
                        </p>
                      </div>
                      <div className="relative" ref={dropdownTaxRef}>
                        <input
                          type="text"
                          value={searchTaxItems}
                          onChange={e => {
                            setSearchTaxItems(e.target.value);
                            setIsTaxItemsDropdownOpen(true);
                          }}
                          onClick={() => setIsTaxItemsDropdownOpen(true)}
                          onKeyDown={e => {
                            validateNoSpecialChars(e);
                            pickerKeyDown(e, 'purchaseTax', isTaxItemsDropdownOpen, setIsTaxItemsDropdownOpen, listTax, handleTaxItemsSelect,
                              taxGroupDisplayName, formValues.productConfigurationData?.purchaseTaxId);
                          }}
                          onBlur={() => setPickerHighlight({ key: '', index: -1 })}
                          role="combobox"
                          aria-expanded={isTaxItemsDropdownOpen}
                          className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                        />
                        {isTaxItemsDropdownOpen && (
                          <div
                            role="listbox"
                            style={{ minHeight: '60px' }}
                            className="absolute z-10 w-full mt-1 bg-white border border-[#EBEBED] rounded-md shadow-lg max-h-60 overflow-auto"
                          >
                            {listTax.map((tax: any, index: number) => (
                              <div
                                key={tax.id}
                                role="option"
                                aria-selected={pickerHighlight.key === 'purchaseTax' && pickerHighlight.index === index}
                                className="px-4 py-2 cursor-pointer hover:bg-[#E7F1F1] text-main"
                                style={pickerHighlight.key === 'purchaseTax' && pickerHighlight.index === index ? { backgroundColor: '#E7F1F1' } : undefined}
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => handleTaxItemsSelect(tax)}
                              >
                                {tax.groupName ?? ''}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
  );

  return (
    <div {...kb.scopeProps}>
      <div className="bg-white">
        <button type="button" className="flex items-center cursor-pointer" {...kb.stepProps(handleBackNavigation)}>
          <div className="w-6 h-6 flex items-center justify-center">
            <BackIcon />
          </div>
          <p
            style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
            className="font-inter"
          >
            {language && language?.['Back'] ? language?.['Back'] : 'Back'}
          </p>
        </button>
      </div>
      {id === 'new' ? (
        <p
          style={{
            margin: isMobile ? '12px 0px 10px' : '20px 0px',
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: '28px',
            color: '#222525',
          }}
          className="font-inter"
        >
          {language && language?.['Add Items details']
            ? language?.['Add Items details']
            : 'Add Items details'}
        </p>
      ) : (
        <p
          style={{
            margin: isMobile ? '12px 0px 10px' : '20px 0px',
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: '28px',
            color: '#222525',
          }}
          className="font-inter"
        >
          {language && language?.['Edit Items details']
            ? language?.['Edit Items details']
            : 'Edit Items details'}
        </p>
      )}
      <div
        className="flex items-start lg:items-center"
        style={{ gap: isMobile ? '10px' : '20px', marginTop: isMobile ? '2px' : '0' }}
      >
        <div
          className={step === 2 ? 'cursor-pointer' : ''}
          style={{ width: isMobile ? 'calc(50% - 5px)' : 'calc(50% - 10px)' }}
          role={step === 2 ? 'button' : undefined}
          tabIndex={step === 2 ? 0 : undefined}
          onClick={() => step === 2 && setStep(1)}
          onKeyDown={e => e.key === 'Enter' && step === 2 && setStep(1)}
        >
          <div
            className=""
            style={{
              minHeight: '4px',
              width: "100%",
              backgroundColor: (step === 1 || step === 2) ? '#0F7679' : '#DBE4E4',
              borderRadius: '20px',
            }}
          ></div>
          <div className="" style={{ marginTop: isMobile ? "6px" : "12px" }}>
            <div className="flex items-center" style={{ gap: '8px' }}>
              {step === 2 && (
                <div
                  className="flex items-center justify-center"
                  style={{ width: '20px', height: '20px' }}
                >
                  <CheckIcon />
                </div>
              )}
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: '#0F7679' }}
              >
                {language && language?.['Step 1'] ? language?.['Step 1'] : 'Step 1'}
              </p>
            </div>
            <p
              className="text-[14px] font-inter leading-[20px] font-medium"
              style={{ color: '#05292A' }}
            >
              {language && language?.['Create Item'] ? language?.['Create Item'] : 'Create Item'}
            </p>
          </div>
        </div>

        {/* Step 2 - Chỉ hiển thị khi searchType không phải 'Asset' hoặc 'Stock Item' */}
        {searchType !== 'Asset' && searchType !== 'Stock Item' && (
          <div
            className={step === 1 ? 'cursor-pointer' : ''}
            style={{ width: isMobile ? 'calc(50% - 5px)' : 'calc(50% - 10px)' }}
            role={step === 1 ? 'button' : undefined}
            tabIndex={step === 1 ? 0 : undefined}
            onClick={() => {
              // Jumping forward runs the same checks as the Next button.
              if (step !== 1) return;
              const step1Errors = collectStep1Errors();
              if (Object.keys(step1Errors).length > 0) {
                setErrors(step1Errors);
                return;
              }
              setStep(2);
            }}
            onKeyDown={e => {
              if (e.key !== 'Enter' || step !== 1) return;
              const step1Errors = collectStep1Errors();
              if (Object.keys(step1Errors).length > 0) {
                setErrors(step1Errors);
                return;
              }
              setStep(2);
            }}
          >
            <div
              className=""
              style={{
                minHeight: '4px',
                width: "100%",
                backgroundColor: step === 2 ? '#0F7679' : '#DBE4E4',
                borderRadius: '20px',
              }}
            ></div>
            <div className="" style={{ marginTop: isMobile ? "6px" : "12px" }}>
              <div className="flex items-center" style={{ gap: '8px' }}>
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: step === 2 ? '#0F7679' : '#819596' }}
                >
                  {language && language?.['Step 2'] ? language?.['Step 2'] : 'Step 2'}
                </p>
              </div>
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: '#05292A' }}
              >
                {language && language?.['Advanced Configuration']
                  ? language?.['Advanced Configuration']
                  : 'Advanced Configuration'}
              </p>
            </div>
          </div>
        )}
      </div>
      <div
        className="scroll-container"
        style={{ height: isMobile ? 'calc(100% - 195px)' : 'calc(100% - 219px)', overflowY: 'scroll', paddingBottom: '16px' }}
      >
        {step === 2 && searchType !== 'Asset' && searchType !== 'Stock Item' ? (
          <>
            <form style={{ maxWidth: '880px', margin: '24px auto 0' }}>
              <div
                style={{
                  padding: '24px',
                  border: '1px solid #DBE4E4',
                  borderRadius: '12px',
                }}
              >
                <p
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    lineHeight: '28px',
                    color: '#222525',
                  }}
                  className="font-inter"
                >
                  {language && language?.['Advanced Configuration']
                    ? language?.['Advanced Configuration']
                    : 'Advanced Configuration'}
                </p>
                <div {...recipeKb.scopeProps}>
                <div className="flex items-end" style={{ gap: '24px', marginTop: '24px', flexWrap: isMobile ? 'wrap' : 'unset' }}>
                  <div className="w-full space-y-2" style={{ width: isMobile ? '100%' : '25%' }}>
                    <p
                      className="text-[14px] font-inter leading-[20px] font-medium"
                      style={{ color: '#4A5353' }}
                    >
                      {language && language?.['Stock Item']
                        ? language?.['Stock Item']
                        : 'Stock Item'}
                    </p>
                    <div className="w-full">
                      <DropdownSearch<{ id: string; productName: string }>
                        refKey={`stock`}
                        refs={{
                          [`stock`]: dropdownStockRef,
                        }}
                        openStates={{
                          [`stock`]: isStockItemsDropdownOpen,
                        }}
                        open={() => setIsStockItemsDropdownOpen(true)}
                        close={() => setIsStockItemsDropdownOpen(false)}
                        data={listStock}
                        labelExtractor={item => item.productName}
                        value={searchStockItems || ''}
                        setValue={setSearchStockItems}
                        onSelect={handleStockItemsSelect}
                        onSearch={value => {
                          handleSubmitSearchStock(value);
                        }}
                        onLoadMore={onLoadMoreStock}
                        hasMore={hasMore.product}
                        loading={loading.product}
                        borderNone={false}
                      />
                    </div>
                  </div>
                  <div className="w-full space-y-2" style={{ width: isMobile ? '100%' : '25%' }}>
                    <p
                      className="text-[14px] font-inter leading-[20px] font-medium"
                      style={{ color: '#4A5353' }}
                    >
                      {language && language?.['Select UOM']
                        ? language?.['Select UOM']
                        : 'Select UOM'}
                    </p>
                    <div className="w-full">
                      <DropdownSearch<{ UOMId: string; uom: { uomName: string } }>
                        refKey={`uomRecipt`}
                        refs={{
                          [`uomRecipt`]: dropdownReciptUOMRef,
                        }}
                        openStates={{
                          [`uomRecipt`]: isUOMDropdownReciptOpen,
                        }}
                        open={() => setIsUOMDropdownReciptOpen(true)}
                        close={() => setIsUOMDropdownReciptOpen(false)}
                        data={listUOMConfiguration}
                        labelExtractor={item => item.uom.uomName}
                        value={searchUOMRecipt || ''}
                        setValue={setSearchUOMRecipt}
                        onSelect={handleUOMReciptSelect}
                        onSearch={value => {
                          handleSearchUOMConfiguration(value);
                        }}
                        // onLoadMore={onLoadMoreUOM}
                        hasMore={hasMore.uom}
                        loading={loading.uom}
                        borderNone={false}
                      />
                    </div>
                    {errors.quantity && <p className="error">{errors.quantity}</p>}
                  </div>
                  <div className="w-full space-y-2" style={{ width: isMobile ? 'calc(50% - 12px)' : '25%' }}>
                    <p
                      className="text-[14px] font-inter leading-[20px] font-medium"
                      style={{ color: '#4A5353' }}
                    >
                      {language && language?.['Quantity'] ? language?.['Quantity'] : 'Quantity'}
                    </p>
                    <div className="w-full">
                      <input
                        value={formValues?.quantity ?? ''}
                        name="quantity"
                        onChange={(e: any) => {
                          handleChange(e);
                        }}
                        className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                      />
                    </div>
                    {errors.quantity && <p className="error">{errors.quantity}</p>}
                  </div>
                  <div className="w-full space-y-2" style={{ width: isMobile ? 'calc(50% - 12px)' : '25%' }}>
                    <p
                      className="text-[14px] font-inter leading-[20px] font-medium"
                      style={{ color: '#4A5353' }}
                    >
                      {language && language?.['Calories'] ? language?.['Calories'] : 'Calories'}
                    </p>
                    <div className="w-full">
                      <input
                        value={formValues?.calories ?? ''}
                        name="calories"
                        onChange={(e: any) => {
                          handleChange(e);
                        }}
                        className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                      />
                    </div>
                    {errors.calories && <p className="error">{errors.calories}</p>}
                  </div>
                  <button
                    type="button"
                    className="cursor-pointer w-fit"
                    {...recipeKb.primaryProps(handleAddStockToRecipes)}
                    style={{
                      width: 'fit-content',
                      padding: '10px 24px',
                      borderRadius: '30px',
                      color: '#0F7679',
                      border: '1px solid #0F7679',
                      fontWeight: '500',
                      fontSize: '14px',
                    }}
                  >
                    {editingIndex !== null
                      ? (language && language?.['Update'] ? language?.['Update'] : 'Update')
                      : (language && language?.['Add'] ? language?.['Add'] : 'Add')}
                  </button>

                  {editingIndex !== null && (
                    <button
                      type="button"
                      className="cursor-pointer w-fit"
                      onClick={handleCancelEdit}
                      style={{
                        width: 'fit-content',
                        padding: '10px 24px',
                        borderRadius: '30px',
                        color: '#6A7878',
                        border: '1px solid #6A7878',
                        fontWeight: '500',
                        fontSize: '14px',
                      }}
                    >
                      {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                    </button>
                  )}
                </div>
                </div>
                {formValues?.recipeData && formValues?.recipeData.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <p
                      style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        lineHeight: '28px',
                        color: '#222525',
                      }}
                      className="font-inter"
                    >
                      {language && language?.['Recipes'] ? language?.['Recipes'] : 'Recipes'}
                    </p>
                    {
                      isMobile ?
                        <div>
                          {formValues?.recipeData.map((value: any, index: any) => {
                            return (
                              <div className="space-y-3" key={value?.uomId} style={{ border: '1px solid #DBE4E4', padding: '16px', borderRadius: "12px" }}>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter"
                                  style={{
                                    color: "#4A5353",
                                    width: `100%`,
                                  }}
                                >
                                  <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                    {language && language?.['Item']
                                      ? language?.['Item']
                                      : 'Item'}
                                  </p>
                                  <p>{value?.productName}</p>
                                </div>
                                <div className="flex items-center">
                                  <div
                                    className="text-[14px] font-medium leading-[18px] font-inter"
                                    style={{
                                      color: "#4A5353",
                                      width: "50%"
                                    }}
                                  >
                                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                      {language && language?.['UOM']
                                        ? language?.['UOM']
                                        : 'UOM'}
                                    </p>
                                    <p>{value?.uomName}</p>
                                  </div>
                                  <div
                                    className="text-[14px] font-medium leading-[18px] font-inter"
                                    style={{
                                      color: "#4A5353",
                                      width: "50%"
                                    }}
                                  >
                                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                      {language && language?.['QTY']
                                        ? language?.['QTY']
                                        : 'QTY'}
                                    </p>
                                    <p>{value?.quantity}</p>
                                  </div>
                                </div>
                                <div className="flex items-center">
                                  <div
                                    className="text-[14px] font-medium leading-[18px] font-inter"
                                    style={{
                                      color: "#4A5353",
                                      width: "50%"
                                    }}
                                  >
                                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                      {language && language?.['Date']
                                        ? language?.['Date']
                                        : 'Date'}
                                    </p>
                                    <p>{formatDate(value?.createdAt)}</p>
                                  </div>
                                  <div
                                    className="text-[14px] font-medium leading-[18px] font-inter"
                                    style={{
                                      color: "#4A5353",
                                      width: "50%"
                                    }}
                                  >
                                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                      {language && language?.['Calories']
                                        ? language?.['Calories']
                                        : 'Calories'}
                                    </p>
                                    <p>{value?.calories}</p>
                                  </div>
                                </div>
                                <div className="flex items-center">
                                  <div
                                    className="text-[14px] font-medium leading-[18px] font-inter"
                                    style={{
                                      color: "#4A5353",
                                      width: "50%"
                                    }}
                                  >
                                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                      {language && language?.['Status']
                                        ? language?.['Status']
                                        : 'Status'}
                                    </p>
                                    <div className="flex items-center gap-x-3">
                                      <div
                                        className={`switch ${value.isActive ? 'on' : 'off'}`}
                                        role="switch"
                                        aria-checked={!!value.isActive}
                                        aria-label={`${language?.['Status'] ?? 'Status'} ${value?.productName ?? ''}`}
                                        tabIndex={0}
                                        onKeyDown={onToggleKey(() => handleToggleRecipe(index))}
                                        onClick={() => {
                                          handleToggleRecipe(index);
                                        }}
                                      >
                                        <div
                                          className={`shadow_toggle slider ${value.isActive ? 'move-right' : 'move-left'
                                            }`}
                                        ></div>
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    className="text-[14px] font-medium leading-[18px] font-inter "
                                    style={{
                                      color: "#4A5353",
                                      width: "50%"
                                    }}
                                  >
                                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                      {language && language?.['Actions']
                                        ? language?.['Actions']
                                        : 'Actions'}
                                    </p>
                                    <div className="flex items-center gap-x-3">
                                      <button
                                        type="button"
                                        className="cursor-pointer"
                                        onClick={() => handleEditRecipe(index)}
                                        aria-label={`${language?.['Edit'] ?? 'Edit'} ${value?.productName ?? ''}`}
                                      >
                                        <EditIcon />
                                      </button>
                                      <button
                                        type="button"
                                        className="cursor-pointer"
                                        onClick={() => handleDeleteRecipe(index)}
                                        aria-label={`${language?.['Delete'] ?? 'Delete'} ${value?.productName ?? ''}`}
                                      >
                                        <RedDeleteIconV2 />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        :
                        <div
                          style={{
                            border: '1px solid #DBE4E4',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            marginTop: '12px',
                            backgroundColor: '#F8F8F8',
                            paddingTop: '8px',
                          }}
                        >
                          <div className="h-8 flex items-center ">
                            <div
                              className="font-medium font-inter flex items-center"
                              style={{
                                color: '#6A7878',
                                fontSize: '14px',
                                padding: '0px 12px',
                                width: `${calculatedWidth * 233}px`,
                                borderTopRightRadius: '12px',
                                borderTopLeftRadius: '12px',
                              }}
                            >
                              <p>{language && language?.['Item'] ? language?.['Item'] : 'Item'}</p>
                            </div>
                            <div
                              className="font-medium font-inter flex items-center"
                              style={{
                                color: '#6A7878',
                                fontSize: '14px',
                                padding: '0px 12px',
                                width: `${calculatedWidth * 233}px`,
                              }}
                            >
                              <p>{language && language?.['UOM'] ? language?.['UOM'] : 'UOM'}</p>
                            </div>
                            <div
                              className="font-medium font-inter flex items-center"
                              style={{
                                color: '#6A7878',
                                fontSize: '14px',
                                padding: '0px 12px',
                                width: `${calculatedWidth * 233}px`,
                              }}
                            >
                              <p>{language && language?.['Qty'] ? language?.['Qty'] : 'Qty'}</p>
                            </div>
                            <div
                              className="font-medium font-inter flex items-center"
                              style={{
                                color: '#6A7878',
                                fontSize: '14px',
                                padding: '0px 12px',
                                width: `${calculatedWidth * 233}px`,
                              }}
                            >
                              <p>{language && language?.['Calories'] ? language?.['Calories'] : 'Calories'}</p>
                            </div>
                            <div
                              className="font-medium font-inter flex items-center"
                              style={{
                                color: '#6A7878',
                                fontSize: '14px',
                                padding: '0px 12px',
                                width: `${calculatedWidth * 233}px`,
                              }}
                            >
                              <p>{language && language?.['Date'] ? language?.['Date'] : 'Date'}</p>
                            </div>
                            <div
                              className="font-medium font-inter flex items-center"
                              style={{
                                color: '#6A7878',
                                fontSize: '14px',
                                padding: '0px 12px',
                                width: `${calculatedWidth * 76}px`,
                              }}
                            >
                              <p>
                                {language && language?.['Status'] ? language?.['Status'] : 'Status'}
                              </p>
                            </div>
                            <div
                              className="font-medium font-inter flex items-center"
                              style={{
                                color: '#6A7878',
                                fontSize: '14px',
                                padding: '0px 12px',
                                width: `${calculatedWidth * 80}px`,
                              }}
                            ></div>
                          </div>
                          {formValues?.recipeData.map((value: any, index: any) => {
                            return (
                              <div key={value?.uomId} className="h-10 flex items-center">
                                <div
                                  className="font-medium font-inter flex items-center"
                                  style={{
                                    color: '#222525',
                                    fontSize: '14px',
                                    padding: '0px 12px',
                                    width: `${calculatedWidth * 233}px`,
                                  }}
                                >
                                  <p>{value?.productName}</p>
                                </div>
                                <div
                                  className="font-medium font-inter flex items-center"
                                  style={{
                                    color: '#222525',
                                    fontSize: '14px',
                                    padding: '0px 12px',
                                    width: `${calculatedWidth * 233}px`,
                                  }}
                                >
                                  <p>{value?.uomName}</p>
                                </div>
                                <div
                                  className="font-medium font-inter flex items-center"
                                  style={{
                                    color: '#222525',
                                    fontSize: '14px',
                                    padding: '0px 12px',
                                    width: `${calculatedWidth * 233}px`,
                                  }}
                                >
                                  <p>{value?.quantity}</p>
                                </div>
                                <div
                                  className="font-medium font-inter flex items-center"
                                  style={{
                                    color: '#222525',
                                    fontSize: '14px',
                                    padding: '0px 12px',
                                    width: `${calculatedWidth * 233}px`,
                                  }}
                                >
                                  <p>{value?.calories}</p>
                                </div>
                                <div
                                  className="font-medium font-inter flex items-center"
                                  style={{
                                    color: '#222525',
                                    fontSize: '14px',
                                    padding: '0px 12px',
                                    width: `${calculatedWidth * 233}px`,
                                  }}
                                >
                                  <p>{formatDate(value?.createdAt)}</p>
                                </div>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                                  style={{
                                    color: '#424242',
                                    padding: '0px 12px',
                                    width: `${calculatedWidth * 76}px`,
                                  }}
                                >
                                  <div className="flex items-center gap-x-3">
                                    <div
                                      className={`switch ${value.isActive ? 'on' : 'off'}`}
                                      role="switch"
                                      aria-checked={!!value.isActive}
                                      aria-label={`${language?.['Status'] ?? 'Status'} ${value?.productName ?? ''}`}
                                      tabIndex={0}
                                      onKeyDown={onToggleKey(() => handleToggleRecipe(index))}
                                      onClick={() => {
                                        handleToggleRecipe(index);
                                      }}
                                    >
                                      <div
                                        className={`shadow_toggle slider ${value.isActive ? 'move-right' : 'move-left'
                                          }`}
                                      ></div>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className="text-xs font-inter flex items-center gap-2"
                                  style={{
                                    color: '#424242',
                                    padding: '0px 12px',
                                    width: `${calculatedWidth * 80}px`,
                                  }}
                                >
                                  <button
                                    type="button"
                                    className="cursor-pointer"
                                    onClick={() => handleEditRecipe(index)}
                                    aria-label={`${language?.['Edit'] ?? 'Edit'} ${value?.productName ?? ''}`}
                                  >
                                    <EditIcon />
                                  </button>
                                  <button
                                    type="button"
                                    className="cursor-pointer"
                                    onClick={() => handleDeleteRecipe(index)}
                                    aria-label={`${language?.['Delete'] ?? 'Delete'} ${value?.productName ?? ''}`}
                                  >
                                    <RedDeleteIconV2 />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                    }
                  </div>
                )}
              </div>
              <div
                className="flex items-start"
                style={{ width: '100%', gap: '24px', marginTop: '24px', flexWrap: 'wrap' }}
              >
                {/* Sales Attributes moved to Step 1 (main create flow). */}
                <div
                  style={{
                    padding: '24px',
                    borderRadius: '12px',
                    width: '100%',
                    backgroundColor: '#F8F8F8',
                    border: '1px solid #DBE4E4',
                  }}
                >
                  <p
                    style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      lineHeight: '28px',
                      color: '#222525',
                    }}
                    className="font-inter"
                  >
                    {language && language?.['Barcodes'] ? language?.['Barcodes'] : 'Barcodes'}
                  </p>
                  <div style={{ marginTop: '16px' }}>
                    <div {...barcodeKb.scopeProps}>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p
                          className="text-[14px] font-inter leading-[20px] font-medium"
                          style={{ color: '#4A5353' }}
                        >
                          {language && language?.['Enter barcode']
                            ? language?.['Enter barcode']
                            : 'Enter barcode'}
                        </p>
                      </div>
                      <div className="w-full flex items-center" style={{ gap: '16px' }}>
                        <div className="relative w-full ">
                          <input
                            value={barcodes}
                            name="barcode"
                            onChange={(e: any) => {
                              setBarcodes(e.target.value);
                            }}
                            onKeyDown={validateOnlyNumbersNotThree}
                            onPaste={handlePasteOnlyNumbers}
                            className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              handleGenerateBarcode();
                            }}
                            className="absolute cursor-pointer top-1/2 -translate-y-1/2 flex items-center justify-center font-inter font-medium"
                            style={{
                              borderRadius: '30px',
                              fontSize: '14px',
                              color: '#0F7679',
                              padding: '6px 10px',
                              backgroundColor: '#0F76791F',
                              right: '8px',
                            }}
                          >
                            Auto Generate
                          </button>
                        </div>
                        <button
                          type="button"
                          className="cursor-pointer w-fit"
                          {...barcodeKb.primaryProps(() => {
                            if (barcodes && String(barcodes).trim()) {
                              setListBarcode((prev: any) => [...prev, String(barcodes).trim()]);
                              setBarcodes('');
                            }
                          })}
                          style={{
                            width: 'fit-content',
                            padding: '10px 24px',
                            borderRadius: '30px',
                            color: '#0F7679',
                            border: '1px solid #0F7679',
                            fontWeight: '500',
                            fontSize: '14px',
                          }}
                        >
                          {language && language?.['Add'] ? language?.['Add'] : 'Add'}
                        </button>
                      </div>
                      {errors.barcode && <p className="error">{errors.barcode}</p>}
                    </div>
                    </div>
                    {listBarcode && listBarcode.length > 0 && (
                      <div
                        className="flex items-center"
                        style={{ marginTop: '16px', gap: '8px', flexWrap: 'wrap', width: '100%' }}
                      >
                        {listBarcode.map((v: any) => {
                          return (
                            <div
                              style={{
                                borderRadius: '30px',
                                padding: '8px 16px',
                                backgroundColor: '#EBF0F0',
                                gap: '12px',
                              }}
                              className="flex items-center"
                            >
                              <div
                                className="font-inter"
                                style={{ fontSize: '14px', fontWeight: '500', color: '#4A5353' }}
                              >
                                {v}
                              </div>
                              <button
                                type="button"
                                className="cursor-pointer"
                                aria-label={`${language?.['Remove'] ?? 'Remove'} ${v}`}
                                onClick={() => {
                                  handleDeleteBarcode(v);
                                }}
                              >
                                <CloseIconV2 />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* {searchType !== 'Sale Item' && (
                
              )} */}
<div className='flex' style={{ width: '100%', gap: '24px', marginTop: '24px', flexWrap: 'wrap' }}>
                  {/* Count configurations */}
                  <div
                    className='space-y-3'
                    style={{
                      padding: '24px',
                      borderRadius: '12px',
                      width: '100%',
                      backgroundColor: '#F8F8F8',
                      border: '1px solid #DBE4E4',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <p
                      className="text-[14px] font-inter leading-[20px] font-medium"
                      style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        lineHeight: '28px',
                        color: '#222525',
                      }}
                    >
                      {language && language?.['Count configurations']
                        ? language?.['Count configurations']
                        : 'Count configurations'}
                    </p>
                    <div >
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#4A5353',
                          marginBottom: '12px',
                          fontFamily: 'Inter',
                          fontWeight: '500',
                        }}
                      >
                        {language && language?.['Is this item countable']
                          ? language?.['Is this item countable']
                          : 'Is this item countable'}
                      </p>
                      <div style={{ display: 'flex', gap: '24px' }}>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="radio"
                            name="countable"
                            checked={formValues?.productConfigurationData.isCountable}
                            onChange={() => handleRadioChange(true)}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                              accentColor: '#0F7679',
                            }}
                          />
                          <span
                            style={{
                              fontSize: '14px',
                              color: '#222525',
                              fontFamily: 'Inter',
                              fontWeight: '500',
                            }}
                          >
                            {language && language?.['Yes']
                              ? language?.['Yes']
                              : 'Yes'}
                          </span>
                        </label>

                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="radio"
                            name="countable"
                            checked={!formValues?.productConfigurationData.isCountable}
                            onChange={() => handleRadioChange(false)}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                              accentColor: '#0F7679',
                            }}
                          />
                          <span
                            style={{
                              fontSize: '14px',
                              color: '#222525',
                              fontFamily: 'Inter',
                              fontWeight: '500',
                            }}
                          >
                            {language && language?.['No']
                              ? language?.['No']
                              : 'No'}
                          </span>
                        </label>
                      </div>
                    </div>
                    {renderPurchaseTaxPicker()}
                  </div>

                  {/* Printing options */}
                  <div
                    style={{
                      padding: '24px',
                      borderRadius: '12px',
                      width: '100%',
                      backgroundColor: '#F8F8F8',
                      border: '1px solid #DBE4E4',
                    }}
                  >
                    <p
                      className="text-[14px] font-inter leading-[20px] font-medium"
                      style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        lineHeight: '28px',
                        color: '#222525',
                        marginBottom: '16px',
                      }}
                    >
                      {language && language?.['Print Destinations']
                        ? language?.['Print Destinations']
                        : 'Print Destinations'}
                    </p>
                    <div className='space-y-3' style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      {listPrintGroups.length === 0 ? (
                        <p style={{ fontSize: '14px', color: '#6A7878', fontFamily: 'Inter' }}>
                          {language?.['No print groups'] ?? 'No print groups available.'}
                        </p>
                      ) : (
                        listPrintGroups.map((pg) => (
                          <label
                            key={pg.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={(formValues?.productConfigurationData?.print_group_ids || []).includes(pg.id)}
                              onChange={() => togglePrintGroup(pg.id)}
                              style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                accentColor: '#0F7679',
                              }}
                            />
                            <span
                              style={{
                                fontSize: '14px',
                                color: '#222525',
                                fontWeight: '500',
                                fontFamily: 'Inter',
                              }}
                            >
                              {pg.name || pg.code}
                              {pg.print_document_type_code ? (
                                <span style={{ color: '#6A7878', fontWeight: 400 }}>{` (${pg.print_document_type_code})`}</span>
                              ) : null}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
            </form>
          </>
        ) :
          step === 1 ?
            (
              <>
                <form style={{ maxWidth: '880px', margin: '0 auto' }}>
                  {/* Photo: prominent near the top, Square/Lightspeed style. */}
                  <div className="" style={{ marginTop: '24px' }}>
                    <div style={{ width: '100%' }}>
                      <p
                        className="font-inter"
                        style={{
                          fontSize: '16px',
                          fontWeight: '500',
                          lineHeight: '28px',
                          color: '#222525',
                          marginBottom: '12px',
                        }}
                      >
                        {language && language?.['Photo'] ? language?.['Photo'] : 'Photo'}
                      </p>
                      <div
                        className="w-full flex items-center justify-center"
                        style={{
                          padding: '40px 12px',
                          backgroundColor: '#FCFDFD',
                          border: `1px dashed ${isDragging ? '#3AC0C4' : '#B8D5D5'}`,
                          borderRadius: '12px',
                        }}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                      >
                        <div className="space-y-4 text-center">
                          <div className="flex items-center justify-center">
                            <UploadIcon />
                          </div>
                          {previewImg || formValues?.image ? (
                            <div className="flex items-center justify-center" style={{ gap: '24px' }}>
                              <button
                                type="button"
                                className="text-[14px] font-inter  font-semibold leading-[18px] cursor-pointer"
                                style={{ color: '#3AC0C4', textDecoration: 'underline', marginTop: '16px' }}
                                onClick={() => {
                                  setShowPopupImage(true);
                                }}
                              >
                                {language && language?.['View Files']
                                  ? language?.['View Files']
                                  : 'View Files'}
                              </button>
                              <button
                                type="button"
                                className="text-[14px] font-inter  font-semibold leading-[18px] cursor-pointer"
                                style={{ color: '#3AC0C4', textDecoration: 'underline', marginTop: '16px' }}
                                onClick={() => {
                                  handleRemoveFile();
                                }}
                              >
                                {language && language?.['Remove Files']
                                  ? language?.['Remove Files']
                                  : 'Remove Files'}
                              </button>
                            </div>
                          ) : (
                            <label
                              htmlFor="fileInput"
                              role="button"
                              tabIndex={0}
                              onKeyDown={onActivateKey(() => document.getElementById('fileInput')?.click())}
                              className="text-[14px] font-inter  font-semibold leading-[18px] cursor-pointer"
                              style={{ color: '#3AC0C4', textDecoration: 'underline', marginTop: '16px' }}
                            >
                              {language && language?.['Upload Files']
                                ? language?.['Upload Files']
                                : 'Upload Files'}
                            </label>
                          )}
                          {(!previewImg || formValues?.image) && (
                            <p className="text-[14px] text-main leading-[20px] font-inter">
                              {fileName
                                ? `File: ${fileName}`
                                : language && language?.['or drag & drop here']
                                  ? language?.['or drag & drop here']
                                  : 'or drag & drop here'}
                            </p>
                          )}
                          <input
                            type="file"
                            accept=".jpeg,.jpg,.heic,.png,.webp"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                            id="fileInput"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Required to Sell: SKU, group, name, type — the identity of the
                      product, shown first and ahead of price. */}
                  <div className="" style={{ marginTop: '24px' }}>
                    <div
                      style={{
                        padding: '24px',
                        border: '1px solid #DBE4E4',
                        borderRadius: '12px',
                        width: '100%',
                      }}
                    >
                      <p
                        className="font-inter"
                        style={{
                          fontSize: '16px',
                          fontWeight: '500',
                          lineHeight: '28px',
                          color: '#222525',
                          marginBottom: '24px',
                        }}
                      >
                        {language && language?.['Required to Sell']
                          ? language?.['Required to Sell']
                          : 'Required to Sell'}
                      </p>
                      <div className="flex items-start" style={{ gap: '24px', flexWrap: 'wrap' }}>
                        <div className="space-y-2" style={{ width: '100%' }}>
                          <div className="flex items-center">
                            <p
                              className="text-[14px] font-inter leading-[20px] font-medium"
                              style={{ color: '#4A5353' }}
                            >
                              {language && language?.['SKU'] ? language?.['SKU'] : 'SKU'}
                              <span style={{ color: '#4A5353', fontWeight: '400' }}>
                                {language && language?.[' (Stock keeping unit)']
                                  ? language?.[' (Stock keeping unit)']
                                  : ' (Stock keeping unit)'}
                              </span>
                            </p>
                          </div>
                          <div className="w-full">
                            <input
                              autoFocus={id === 'new' && !isMobile}
                              value={formValues?.sku}
                              name="sku"
                              {...limitProps(limits, 'sku')}
                              placeholder={language && language?.['Leave blank to auto-generate'] ? language?.['Leave blank to auto-generate'] : 'Leave blank to auto-generate'}
                              onChange={(e: any) => {
                                handleChange(e);
                              }}
                              className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                            />
                            <FieldCounter value={formValues?.sku} max={limits.sku?.maxLength} />
                          </div>
                          {errors.sku && <p className="error">{errors.sku}</p>}
                        </div>
                        <div className="space-y-2" style={{ width: '100%' }}>
                          <div className="flex items-center">
                            <p
                              className="text-[14px] font-inter leading-[20px] font-medium"
                              style={{ color: '#4A5353' }}
                            >
                              {language && language?.['Item Group']
                                ? language?.['Item Group']
                                : 'Item Group'}
                            </p>
                            <p
                              className="text-[14px] font-inter leading-[20px] font-medium"
                              style={{ color: '#BA0E13' }}
                            >
                              {language && language?.['*'] ? language?.['*'] : '*'}
                            </p>
                          </div>
                          <div className="w-full">
                            <DropdownSearch<{ id: string; groupName: string }>
                              refKey={`group`}
                              refs={{
                                [`group`]: dropdownGroupRef,
                              }}
                              openStates={{
                                [`group`]: isGroupItemsDropdownOpen,
                              }}
                              open={() => setIsGroupItemsDropdownOpen(true)}
                              close={() => setIsGroupItemsDropdownOpen(false)}
                              data={groupItems}
                              labelExtractor={item => item.groupName}
                              value={searchGroupItems || ''}
                              setValue={setSearchGroupItems}
                              onSelect={handleGroupItemsSelect}
                              onSearch={value => {
                                handleSearchGroup(value);
                              }}
                              onLoadMore={onLoadMoreGroup}
                              hasMore={hasMore.group}
                              loading={loading.group}
                              borderNone={false}
                            />
                            {errors.productGroupId && <p className="error">{errors.productGroupId}</p>}
                          </div>
                        </div>
                        <div className="space-y-2" style={{ width: '100%' }}>
                          <div className="flex items-center">
                            <p
                              className="text-[14px] font-inter leading-[20px] font-medium"
                              style={{ color: '#4A5353' }}
                            >
                              {language && language?.['Item Name']
                                ? language?.['Item Name']
                                : 'Item Name'}
                            </p>
                            <p
                              className="text-[14px] font-inter leading-[20px] font-medium"
                              style={{ color: '#BA0E13' }}
                            >
                              {language && language?.['*'] ? language?.['*'] : '*'}
                            </p>
                          </div>
                          <div className="w-full">
                            <input
                              type="text"
                              name="productName"
                              {...limitProps(limits, 'productName')}
                              value={formValues?.productName}
                              onChange={(e: any) => {
                                handleChange(e);
                              }}
                              className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                            />
                            <FieldCounter value={formValues?.productName} max={limits.productName?.maxLength} />
                          </div>
                          {errors.productName && <p className="error">{errors.productName}</p>}
                        </div>
                        <div className="space-y-2" style={{ width: '100%' }}>
                          <div className="flex items-center">
                            <p
                              className="text-[14px] font-inter leading-[20px] font-medium"
                              style={{ color: '#4A5353' }}
                            >
                              {language && language?.['Item Type']
                                ? language?.['Item Type']
                                : 'Item Type'}
                            </p>
                            <p
                              className="text-[14px] font-inter leading-[20px] font-medium"
                              style={{ color: '#BA0E13' }}
                            >
                              {language && language?.['*'] ? language?.['*'] : '*'}
                            </p>
                          </div>
                          <div className="w-full">
                            <DropdownSearch<{ value: number; name: string }>
                              refKey="type"
                              refs={{ type: dropdownTypeRef }}
                              openStates={{ type: isTypeDropdownOpen }}
                              open={key => key === 'type' && setIsTypeDropdownOpen(true)}
                              close={key => key === 'type' && setIsTypeDropdownOpen(false)}
                              data={listType}
                              labelExtractor={item => (language?.[item.name] ?? item.name)}
                              value={searchType}
                              setValue={setSearchType}
                              onSelect={handleTypeSelect}
                              error={errors.transactionMonthsId}
                              borderNone={false}
                            />
                          </div>
                          {errors.type && <p className="error">{errors.type}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Selling: price is one of the first things a shop owner sets.
                      Single prominent price + tax on create; the full historical
                      price table is reserved for editing an existing item. */}
                  {searchType !== 'Stock Item' && id === 'new' && (
                    <div className="" style={{ marginTop: '24px' }}>
                      <div
                        style={{
                          padding: '24px',
                          border: '1px solid #DBE4E4',
                          borderRadius: '12px',
                          width: '100%',
                        }}
                      >
                        <p
                          className="font-inter"
                          style={{
                            fontSize: '16px',
                            fontWeight: '500',
                            lineHeight: '28px',
                            color: '#222525',
                            marginBottom: '24px',
                          }}
                        >
                          {language && language?.['Selling'] ? language?.['Selling'] : 'Selling'}
                        </p>
                        <div className="flex items-center" style={{ gap: '24px', flexWrap: 'wrap' }}>
                          <div className="space-y-2" style={{ width: '100%' }}>
                            <div className="flex items-center">
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#4A5353' }}
                              >
                                {language && language?.['Selling Price']
                                  ? language?.['Selling Price']
                                  : 'Selling Price'}
                              </p>
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#BA0E13' }}
                              >
                                {language && language?.['*'] ? language?.['*'] : '*'}
                              </p>
                            </div>
                            <div className="w-full">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={rows[0]?.price ?? ''}
                                onKeyDown={validateDecimalNumer}
                                onChange={(e: any) =>
                                  setRows((prev: any[]) => {
                                    const next = prev.length
                                      ? [...prev]
                                      : [
                                          {
                                            cost: 0,
                                            validFrom: new Date(),
                                            validTo: getDateAfterMonths(24),
                                            isActive: true,
                                            priceType: PriceType.Regular,
                                          },
                                        ];
                                    next[0] = { ...next[0], price: e.target.value };
                                    return next;
                                  })
                                }
                                className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                              />
                            </div>
                          </div>
                          <div className="space-y-2" style={{ width: '100%' }}>
                            <div className="flex items-center">
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#4A5353' }}
                              >
                                {language && language?.['Price type']
                                  ? language?.['Price type']
                                  : 'Price type'}
                              </p>
                            </div>
                            <div className="w-full">
                              <select
                                className="text-[14px] font-inter text-input outline-none bg-transparent w-full"
                                style={{ padding: '12px', border: '1px solid #DBE4E4', borderRadius: 12 }}
                                value={rows[0]?.priceType ?? PriceType.Regular}
                                onChange={(e: any) => {
                                  const value = Number(e.target.value);
                                  setRows((prev: any[]) => {
                                    const next = prev.length
                                      ? [...prev]
                                      : [
                                          {
                                            price: '',
                                            cost: 0,
                                            validFrom: new Date(),
                                            validTo: getDateAfterMonths(24),
                                            isActive: true,
                                            priceType: PriceType.Regular,
                                          },
                                        ];
                                    next[0] = { ...next[0], priceType: value };
                                    return next;
                                  });
                                }}
                              >
                                {PRICE_TYPE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {language && language?.[opt.name]
                                      ? language?.[opt.name]
                                      : opt.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="space-y-2" style={{ width: '100%' }}>
                            <div className="flex items-center">
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#4A5353' }}
                              >
                                {language && language?.['Sales Tax Rate']
                                  ? language?.['Sales Tax Rate']
                                  : 'Sales Tax Rate'}
                              </p>
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#BA0E13' }}
                              >
                                {language && language?.['*'] ? language?.['*'] : '*'}
                              </p>
                            </div>
                            <div className="w-full">
                              <DropdownSearch<any>
                                refKey="sellingTax"
                                refs={{ sellingTax: sellingTaxRef }}
                                openStates={{ sellingTax: isSellingTaxOpen }}
                                open={() => setIsSellingTaxOpen(true)}
                                close={() => setIsSellingTaxOpen(false)}
                                data={listTax}
                                labelExtractor={(item: any) =>
                                  taxGroupDisplayName(item) || (item.groupName ?? '')
                                }
                                value={searchTaxMap[0] ?? ''}
                                setValue={(val: any) =>
                                  setSearchTaxMap((prev: any) => ({ ...prev, 0: val }))
                                }
                                onSelect={(tax: any) => handleTaxSelect(tax, 0)}
                                borderNone={false}
                              />
                            </div>
                          </div>
                          <div className="space-y-2" style={{ width: '100%' }}>
                            <div className="flex items-center">
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#4A5353' }}
                              >
                                {language && language?.['Cost'] ? language?.['Cost'] : 'Cost'}
                              </p>
                            </div>
                            <div className="w-full">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={rows[0]?.cost ?? ''}
                                onKeyDown={validateDecimalNumer}
                                onChange={(e: any) =>
                                  setRows((prev: any[]) => {
                                    const next = prev.length
                                      ? [...prev]
                                      : [
                                          {
                                            price: '',
                                            validFrom: new Date(),
                                            validTo: getDateAfterMonths(24),
                                            isActive: true,
                                            priceType: PriceType.Regular,
                                          },
                                        ];
                                    next[0] = { ...next[0], cost: e.target.value };
                                    return next;
                                  })
                                }
                                className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                              />
                            </div>
                          </div>
                          <div className="space-y-2" style={{ width: '100%' }}>
                            <div className="flex items-center">
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#4A5353' }}
                              >
                                {language && language?.['Valid From']
                                  ? language?.['Valid From']
                                  : 'Valid From'}
                              </p>
                            </div>
                            <div className="w-full">
                              <DatePicker
                                dateFormat="dd-MM-yyyy"
                                selected={rows[0]?.validFrom ? new Date(rows[0].validFrom) : null}
                                onChange={(date: any) =>
                                  setRows((prev: any[]) => {
                                    const next = prev.length
                                      ? [...prev]
                                      : [
                                          {
                                            price: '',
                                            cost: 0,
                                            validTo: getDateAfterMonths(24),
                                            isActive: true,
                                            priceType: PriceType.Regular,
                                          },
                                        ];
                                    next[0] = { ...next[0], validFrom: date };
                                    return next;
                                  })
                                }
                                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#DBE4E4] p-3 rounded-xl  outline-none focus-none bg-transparent w-full"
                              />
                            </div>
                          </div>
                          <div className="space-y-2" style={{ width: '100%' }}>
                            <div className="flex items-center">
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#4A5353' }}
                              >
                                {language && language?.['Valid To'] ? language?.['Valid To'] : 'Valid To'}
                              </p>
                            </div>
                            <div className="w-full">
                              <DatePicker
                                dateFormat="dd-MM-yyyy"
                                selected={rows[0]?.validTo ? new Date(rows[0].validTo) : null}
                                onChange={(date: any) =>
                                  setRows((prev: any[]) => {
                                    const next = prev.length
                                      ? [...prev]
                                      : [
                                          {
                                            price: '',
                                            cost: 0,
                                            validFrom: new Date(),
                                            isActive: true,
                                            priceType: PriceType.Regular,
                                          },
                                        ];
                                    next[0] = { ...next[0], validTo: date };
                                    return next;
                                  })
                                }
                                className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#DBE4E4] p-3 rounded-xl  outline-none focus-none bg-transparent w-full"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Sales Attributes: decide where the item appears in POS, so
                      they belong in the main flow, not under advanced settings. */}
                  {searchType !== 'Asset' && searchType !== 'Stock Item' && (
                    <div className="" style={{ marginTop: '24px' }}>
                      <div
                        style={{
                          padding: '24px',
                          border: '1px solid #DBE4E4',
                          borderRadius: '12px',
                          width: '100%',
                        }}
                      >
                        <p
                          className="font-inter"
                          style={{
                            fontSize: '16px',
                            fontWeight: '500',
                            lineHeight: '28px',
                            color: '#222525',
                            marginBottom: '16px',
                          }}
                        >
                          {language && language?.['Sales Attribute(s)']
                            ? language?.['Sales Attribute(s)']
                            : 'Sales Attribute(s)'}
                        </p>
                        <div className="w-full">
                          <DropdownSearch<any>
                            refKey="salesAttributes"
                            refs={{ salesAttributes: dropdownAttributesRef }}
                            openStates={{ salesAttributes: isAttributesItemsDropdownOpen }}
                            open={() => setIsAttributesItemsDropdownOpen(true)}
                            close={() => setIsAttributesItemsDropdownOpen(false)}
                            data={listAttributes.filter(
                              (attribute: any) =>
                                !listAttributesSelect.some((selected: any) => selected.id === attribute.id),
                            )}
                            labelExtractor={(item: any) => item.attributeName ?? ''}
                            value={searchAttributesItems || ''}
                            setValue={setSearchAttributesItems}
                            onSelect={handleAttributesItemsSelect}
                            onSearch={handleSearchAttributes}
                            onLoadMore={onLoadMoreAttributes}
                            hasMore={hasMore.attributes}
                            loading={loading.attributes}
                            borderNone={false}
                            clearOnSelect
                            placeholder={
                              language && language?.['Search'] ? language['Search'] : 'Search'
                            }
                          />
                        </div>
                        {listAttributesSelect.length > 0 && (
                          <div
                            className="flex items-center"
                            style={{ gap: '8px', width: '100%', marginTop: '16px', flexWrap: 'wrap' }}
                          >
                            {listAttributesSelect
                              .filter((val: any) => val.isSelect)
                              .map((v: any) => {
                                return (
                                  <div
                                    key={v?.id}
                                    className="flex items-center w-fit"
                                    style={{
                                      borderRadius: '30px',
                                      padding: '8px 16px',
                                      backgroundColor: '#EBF0F0',
                                      gap: '12px',
                                    }}
                                  >
                                    <span
                                      className="font-inter"
                                      style={{ fontSize: '14px', fontWeight: '500', color: '#4A5353' }}
                                    >
                                      {v?.attributeName}
                                    </span>
                                    <button
                                      type="button"
                                      className="cursor-pointer"
                                      aria-label={`${language?.['Remove'] ?? 'Remove'} ${v?.attributeName ?? ''}`}
                                      onClick={() => {
                                        handleRemoveAttributes(v);
                                      }}
                                    >
                                      <CloseIconV2 />
                                    </button>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Allergens: sits directly after Sales Attribute(s) and
                      follows the same visibility rule. Fixed list of the
                      standard allergens — tap a pill to cycle
                      none -> contains -> may contain -> none. Entirely
                      optional. Every language?.['...'] lookup below is kept
                      intact on one line so scripts/extract-i18n-keys.js can
                      see the key. */}
                  {searchType !== 'Asset' &&
                    searchType !== 'Stock Item' &&
                    listAllergens.length > 0 && (
                      <div className="" style={{ marginTop: '24px' }}>
                        <div
                          style={{
                            padding: '24px',
                            border: '1px solid #DBE4E4',
                            borderRadius: '12px',
                            width: '100%',
                          }}
                        >
                          <p
                            className="font-inter"
                            id="allergens-section-title"
                            style={{
                              fontSize: '16px',
                              fontWeight: '500',
                              lineHeight: '28px',
                              color: '#222525',
                              marginBottom: '4px',
                            }}
                          >
                            {language && language?.['Allergens'] ? language?.['Allergens'] : 'Allergens'}
                          </p>
                          <p
                            className="font-inter"
                            id="allergens-section-help"
                            style={{
                              fontSize: '13px',
                              lineHeight: '20px',
                              color: '#898989',
                              marginBottom: '16px',
                            }}
                          >
                            {language && language?.['Tap to mark what this item contains. Tap again for may contain, again to clear.'] ? language?.['Tap to mark what this item contains. Tap again for may contain, again to clear.'] : 'Tap to mark what this item contains. Tap again for may contain, again to clear.'}
                          </p>
                          <div
                            className="flex flex-wrap"
                            role="group"
                            aria-labelledby="allergens-section-title"
                            aria-describedby="allergens-section-help"
                            style={{ gap: '8px' }}
                          >
                            {listAllergens.map((allergen) => {
                              const level = selectedAllergens[allergen.id];
                              const isContains = level === ALLERGEN_LEVEL.CONTAINS;
                              const isMayContain = level === ALLERGEN_LEVEL.MAY_CONTAIN;
                              const label = (language && language?.[allergen.name]) || allergen.name;
                              const mayContainText = language && language?.['may contain'] ? language?.['may contain'] : 'may contain';
                              const containsText = language && language?.['contains'] ? language?.['contains'] : 'contains';
                              const notSetText = language && language?.['not set'] ? language?.['not set'] : 'not set';
                              const stateText = isContains ? containsText : isMayContain ? mayContainText : notSetText;
                              return (
                                <button
                                  key={allergen.id}
                                  type="button"
                                  aria-label={label + ': ' + stateText}
                                  onClick={() => handleToggleAllergen(allergen.id)}
                                  className="font-inter"
                                  style={{
                                    fontSize: '13.5px',
                                    lineHeight: '20px',
                                    padding: isMobile ? '11px 14px' : '7px 14px',
                                    borderRadius: '999px',
                                    cursor: 'pointer',
                                    fontWeight: level !== undefined ? 600 : 400,
                                    border: isContains
                                      ? '1.5px solid #B4231F'
                                      : isMayContain
                                        ? '1.5px dashed #8A6A16'
                                        : '1.5px solid #DBE4E4',
                                    background: isContains
                                      ? '#FBEAE9'
                                      : isMayContain
                                        ? '#FBF3DD'
                                        : 'transparent',
                                    color: isContains
                                      ? '#B4231F'
                                      : isMayContain
                                        ? '#8A6A16'
                                        : '#4A5353',
                                  }}
                                >
                                  {label}
                                  {isMayContain && (
                                    <span
                                      style={{
                                        fontSize: '11px',
                                        marginLeft: '5px',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {mayContainText}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  <div className="" style={{ marginTop: '24px' }}>
                    <div
                      style={{
                        padding: '24px',
                        border: '1px solid #DBE4E4',
                        borderRadius: '12px',
                        gap: "24px",
                        width: '100%',
                      }}
                    >
                      <p
                        style={{
                          fontSize: '16px',
                          fontWeight: '500',
                          lineHeight: '28px',
                          color: '#222525',
                          marginBottom: '24px',
                        }}
                        className="font-inter"
                      >
                        {language && language?.['Optional Details']
                          ? language?.['Optional Details']
                          : 'Optional Details'}
                      </p>
                      <div className="space-y-2" style={{ marginTop: '24px' }}>
                        <div className="flex items-center justify-between">
                          <p
                            className="text-[14px] font-inter leading-[20px] font-medium"
                            style={{ color: '#4A5353' }}
                          >
                            {language && language?.['Display Name']
                              ? language?.['Display Name']
                              : 'Display Name'}
                          </p>
                        </div>
                        <div className="w-full">
                          <input
                            type="text"
                            name="displayName"
                            {...limitProps(limits, 'displayName')}
                            aria-describedby="displayName-hint"
                            value={formValues?.displayName}
                            onChange={(e: any) => {
                              handleChange(e);
                            }}
                            className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                          />
                          <FieldCounter value={formValues?.displayName} max={limits.displayName?.maxLength} />
                        </div>
                        <p
                          id="displayName-hint"
                          className="text-[12px] font-inter leading-[16px]"
                          style={{ color: '#6A7878' }}
                        >
                          {language && language?.['Displayed on POS or receipts if different from Item Name.']
                            ? language?.['Displayed on POS or receipts if different from Item Name.']
                            : 'Displayed on POS or receipts if different from Item Name.'}
                        </p>
                        {errors.displayName && <p className="error">{errors.displayName}</p>}
                      </div>
                      <div className="flex items-center" style={{ gap: '24px', flexWrap: "wrap" }}>
                        <div style={{ marginTop: '24px', width: '100%' }} className="space-y-2">
                          <p
                            className="text-[14px] font-inter leading-[20px] font-medium"
                            style={{ color: '#4A5353' }}
                          >
                            {language && language?.['Course'] ? language?.['Course'] : 'Course'}
                          </p>
                          <div className="w-full">
                            <DropdownSearch<{ id: string | null; name: string }>
                              refKey="course"
                              refs={{ course: dropdownCourseRef }}
                              openStates={{ course: isCourseDropdownOpen }}
                              open={() => setIsCourseDropdownOpen(true)}
                              close={() => setIsCourseDropdownOpen(false)}
                              data={[{ id: null, name: language?.['None'] ?? 'None' }, ...listCourses]}
                              labelExtractor={item => item.name}
                              value={searchCourse || ''}
                              setValue={setSearchCourse}
                              onSelect={item => handleCourseSelect(item?.id != null ? item : null)}
                              hasMore={false}
                              loading={false}
                              borderNone={false}
                              placeholder={language?.['None'] ?? 'None'}
                            />
                          </div>
                        </div>
                      </div>
                      {searchType !== 'Stock Item' && (
                        <div style={{ marginTop: '24px', width: '100%' }} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <p
                              className="text-[14px] font-inter leading-[20px] font-medium"
                              style={{ color: '#4A5353' }}
                            >
                              {language && language?.['Maximum Discount %']
                                ? language?.['Maximum Discount %']
                                : 'Maximum Discount %'}
                            </p>
                          </div>
                            <div className="w-full">
                              <input
                                value={formValues?.maxDiscount}
                              onKeyDown={validateOnlyNumbersNotThree}
                              name="maxDiscount"
                              onChange={(e: any) => {
                                handleChange(e);
                              }}
                              className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                            />
                          </div>
                          {errors.maxDiscount && <p className="error">{errors.maxDiscount}</p>}
                        </div>
                      )}
                      {searchType !== 'Sale Item' && (
                        <>
                          <div
                            className="w-full space-y-2"
                            style={{ marginTop: '24px', width: '100%', marginBottom: '6px' }}
                          >
                            <div className="flex items-center">
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#4A5353' }}
                              >
                                {language && language?.['Base UOM']
                                  ? language?.['Base UOM']
                                  : 'Base UOM'}
                                <span style={{ color: "#4A5353", fontWeight: '400' }}>{language && language?.[' (Unit of measure)']
                                  ? language?.[' (Unit of measure)']
                                  : ' (Unit of measure)'}</span>
                              </p>
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#BA0E13' }}
                              >
                                {language && language?.['*'] ? language?.['*'] : '*'}
                              </p>
                            </div>

                            <div className="w-full">
                              <DropdownSearch<{ id: string; uomName: string }>
                                refKey={`UOMBase`}
                                refs={{
                                  [`UOMBase`]: dropdownUOMRef,
                                }}
                                openStates={{
                                  [`UOMBase`]: isUOMDropdownOpen,
                                }}
                                open={() => setIsUOMDropdownOpen(true)}
                                close={() => setIsUOMDropdownOpen(false)}
                                data={listUOM}
                                labelExtractor={item => item.uomName}
                                value={searchUOM || ''}
                                setValue={setSearchUOM}
                                onSelect={handleUOMSelect}
                                onSearch={value => {
                                  handleSearchUOM(value);
                                }}
                                onLoadMore={onLoadMoreUOM}
                                hasMore={hasMore.uom}
                                loading={loading.uom}
                                borderNone={false}
                              />
                              {errors.baseUOMId && <p className="error">{errors.baseUOMId}</p>}
                            </div>
                            <p
                              className="text-[12px] font-inter leading-[16px]"
                              style={{ color: '#6A7878' }}
                            >
                              {language && language?.['The main unit used to sell or count this item.']
                                ? language?.['The main unit used to sell or count this item.']
                                : 'The main unit used to sell or count this item.'}
                            </p>
                          </div>
                          {searchType === 'Stock Item' && (
                            <div style={{ marginTop: '24px' }}>{renderPurchaseTaxPicker()}</div>
                          )}
                          {formValues?.alternativeIds.length > 0 &&
                            formValues?.alternativeIds.map((v: any, index: any) => {
                              return (
                                <div
                                  className="flex items-center justify-between"
                                  style={{
                                    gap: '24px',
                                    padding: '6px 12px',
                                    borderBottom:
                                      index < formValues?.alternativeIds.length
                                        ? '1px solid #DBE4E4'
                                        : '',
                                  }}
                                >
                                  <div className="flex items-center w-full justify-between">
                                    <p
                                      className="text-[14px] font-inter"
                                      style={{ fontWeight: '600', color: '#222525' }}
                                    >
                                      {v?.uomName}
                                    </p>
                                    <div className="flex items-center" style={{ gap: '8px' }}>
                                      {v.allowRecipe && (
                                        <div
                                          style={{
                                            padding: '4px 8px',
                                            borderRadius: '8px',
                                            backgroundColor: '#EBF0F0',
                                          }}
                                        >
                                          <p
                                            className="font-inter"
                                            style={{
                                              fontSize: '14px',
                                              color: '#05292A',
                                              fontWeight: '500',
                                            }}
                                          >
                                            {language && language?.['Receipt']
                                              ? language?.['Receipt']
                                              : 'Receipt'}
                                          </p>
                                        </div>
                                      )}
                                      {v.allowOrdering && (
                                        <div
                                          style={{
                                            padding: '4px 8px',
                                            borderRadius: '8px',
                                            backgroundColor: '#EBF0F0',
                                          }}
                                        >
                                          <p
                                            className="font-inter"
                                            style={{
                                              fontSize: '14px',
                                              color: '#05292A',
                                              fontWeight: '500',
                                            }}
                                          >
                                            {language && language?.['Order']
                                              ? language?.['Order']
                                              : 'Order'}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center" style={{ gap: '8px' }}>
                                    <button
                                      type="button"
                                      className="flex items-center justify-center cursor-pointer"
                                      aria-label={`${language?.['Delete'] ?? 'Delete'} ${v?.uomName ?? ''}`}
                                      onClick={() => {
                                        setIdSelect(v.UOMId);
                                        setShowDelete(true);
                                      }}
                                      style={{ width: '40px', height: '40px' }}
                                    >
                                      <DeleteIconV2 />
                                    </button>
                                    <button
                                      type="button"
                                      className="flex items-center justify-center cursor-pointer"
                                      aria-label={`${language?.['Edit'] ?? 'Edit'} ${v?.uomName ?? ''}`}
                                      onClick={() => {
                                        handleEditAlt(v);
                                      }}
                                      style={{ width: '40px', height: '40px' }}
                                    >
                                      <EditIconV2 />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          <button
                            type="button"
                            className="flex items-center cursor-pointer w-fit"
                            style={{ gap: '6px', padding: '6px 8px', marginTop: '12px' }}
                            onClick={() => {
                              setShowAlt(true);
                            }}
                          >
                            <div
                              className="flex items-center justify-center"
                              style={{ width: '24px', height: '24px' }}
                            >
                              <PlusGreenIcon />
                            </div>
                            <p
                              className="font-inter text-[14px] font-medium"
                              style={{ color: '#0F7679' }}
                            >
                              {language && language?.['Add alternative UOM']
                                ? language?.['Add alternative UOM']
                                : 'Add alternative UOM'}
                            </p>
                          </button>
                        </>
                      )}
                    </div>

                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowAdvanced(v => !v)}
                      onKeyDown={e => e.key === 'Enter' && setShowAdvanced(v => !v)}
                      className="cursor-pointer flex items-center"
                      style={{ marginTop: '24px', gap: '6px', width: 'fit-content' }}
                    >
                      <p className="text-[14px] font-inter font-medium" style={{ color: '#0F7679' }}>
                        {showAdvanced
                          ? language && language?.['Hide advanced options']
                            ? language?.['Hide advanced options']
                            : 'Hide advanced options'
                          : language && language?.['Advanced options']
                            ? language?.['Advanced options']
                            : 'Advanced options'}
                      </p>
                    </div>
                    <div className='flex items-start' style={{ width: '100%', gap: '24px', flexWrap: 'wrap', marginTop: "24px", display: showAdvanced ? 'flex' : 'none' }}>
                      {searchType !== 'Stock Item' && (
                        <div
                          style={{
                            padding: '16px',
                            width: '100%',
                            border: '1px solid #DBE4E4',
                            borderRadius: '12px',
                            backgroundColor: '#F8F8F8',
                          }}
                        >
                          <p
                            style={{
                              fontSize: '16px',
                              fontWeight: '500',
                              lineHeight: '28px',
                              color: '#222525',
                            }}
                            className="font-inter"
                          >
                            {language && language?.['Availability']
                              ? language?.['Availability']
                              : 'Availability'}
                          </p>
                          <div className='flex items-center' style={{ marginTop: '16px', flexWrap: 'wrap', gap: '24px' }}>
                            <div className="space-y-2" style={{ width: '100%' }}>
                              <div className="flex items-center justify-between">
                                <p
                                  className="text-[14px] font-inter leading-[20px] font-medium"
                                  style={{ color: '#4A5353' }}
                                >
                                  {language && language?.['On Sale Date']
                                    ? language?.['On Sale Date']
                                    : 'On Sale Date'}
                                </p>
                              </div>
                              <div className="w-full">
                                <DatePicker
                                  showTimeSelect
                                  dateFormat="dd-MM-yyyy"
                                  placeholderText={
                                    language && language?.['Enter a starting date']
                                      ? language?.['Enter a starting date']
                                      : 'Enter a starting date'
                                  }
                                  name="startTime"
                                  className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full"
                                  selected={startDate}
                                  onChange={e => handleStartDate(e)}
                                />
                              </div>
                            </div>
                            <div className="space-y-2" style={{ width: '100%' }} >
                              <div className="flex items-center justify-between">
                                <p
                                  className="text-[14px] font-inter leading-[20px] font-medium"
                                  style={{ color: '#4A5353' }}
                                >
                                  {language && language?.['Off Sale Date']
                                    ? language?.['Off Sale Date']
                                    : 'Off Sale Date'}
                                </p>
                              </div>
                              <div className="w-full">
                                <DatePicker
                                  showTimeSelect
                                  dateFormat="dd-MM-yyyy"
                                  placeholderText={
                                    language && language?.['Enter a end date']
                                      ? language?.['Enter a end date']
                                      : 'Enter a end date'
                                  }
                                  name="startTime"
                                  className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full"
                                  selected={contractExpiration}
                                  onChange={e => handleContractExpiration(e)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div
                        style={{
                          padding: '16px',
                          width: '100%',
                          border: '1px solid #DBE4E4',
                          borderRadius: '12px',
                          backgroundColor: '#F8F8F8',
                        }}
                      >
                        <div className="flex items-center" style={{ gap: '8px' }}>
                          <p
                            style={{
                              fontSize: '16px',
                              fontWeight: '500',
                              lineHeight: '28px',
                              color: '#222525',
                            }}
                            className="font-inter"
                          >
                            {language && language?.['Advanced Accounting']
                              ? language?.['Advanced Accounting']
                              : 'Advanced Accounting'}
                          </p>
                        </div>
                        <div className='flex items-center' style={{ marginTop: '16px', flexWrap: 'wrap', gap: '24px' }}>
                          <div className="space-y-2" style={{ width: '100%' }}>
                            <div className="flex items-center">
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#4A5353' }}
                              >
                                {language && language?.['Expense Code']
                                  ? language?.['Expense Code']
                                  : 'Expense Code'}
                              </p>
                              {/* Expense Code is optional — the red `*` was
                                  removed alongside the matching `is required`
                                  guard in handleSubmit. See the comment in
                                  handleSubmit for the full rationale. */}
                            </div>
                            <DropdownSearch<{ id: string; accountCode: string; accountName: string }>
                              refKey={`expenseCode`}
                              refs={{
                                [`expenseCode`]: dropdownExpenseRef,
                              }}
                              openStates={{
                                [`expenseCode`]: isExpenseDropdownOpen,
                              }}
                              open={() => setIsExpenseDropdownOpen(true)}
                              close={() => setIsExpenseDropdownOpen(false)}
                              data={listSystem}
                              labelExtractor={item => item.accountCode + ' - ' + item.accountName}
                              value={searchExpense || ''}
                              setValue={setSearchExpense}
                              onSelect={handleExpenseSelect}
                              error={errors.chartsOfAccountsExpenseId}
                              loading={loading.systemAccount}
                              borderNone={false}
                            />
                          </div>
                          <div className="space-y-2" style={{ width: '100%' }}>
                            <div className="flex items-center">
                              <p
                                className="text-[14px] font-inter leading-[20px] font-medium"
                                style={{ color: '#4A5353' }}
                              >
                                {language && language?.['Income Code']
                                  ? language?.['Income Code']
                                  : 'Income Code'}
                              </p>
                              {/* Income Code is optional — see Expense Code
                                  comment above for the rationale. */}
                            </div>
                            <DropdownSearch<{ id: string; accountCode: string; accountName: string }>
                              refKey={`incomeCode`}
                              refs={{
                                [`incomeCode`]: dropdownIncomeRef,
                              }}
                              openStates={{
                                [`incomeCode`]: isIncomeDropdownOpen,
                              }}
                              open={() => setIsIncomeDropdownOpen(true)}
                              close={() => setIsIncomeDropdownOpen(false)}
                              data={listSystem}
                              labelExtractor={item => item.accountCode + ' - ' + item.accountName}
                              value={searchIncome || ''}
                              setValue={setSearchIncome}
                              onSelect={handleIncomeSelect}
                              error={errors.chartsOfAccountsIncomeId}
                              loading={loading.systemAccount}
                              borderNone={false}
                            />
                          </div>
                          {searchType === 'Asset' && (
                            <>
                              <div style={{ marginTop: '12px' }} className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <p
                                    className="text-[14px] font-inter leading-[20px] font-medium"
                                    style={{ color: '#4A5353' }}
                                  >
                                    {language && language?.['Depreciation Years']
                                      ? language?.['Depreciation Years']
                                      : 'Depreciation Years'}
                                  </p>
                                </div>
                                <div className="w-full">
                                  <input
                                    value={formValues?.deprecationYears}
                                    onKeyDown={validateOnlyNumbers}
                                    name="deprecationYears"
                                    onChange={(e: any) => {
                                      handleChange(e);
                                    }}
                                    className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                                  />
                                </div>
                                {errors.deprecationYears && (
                                  <p className="error">{errors.deprecationYears}</p>
                                )}
                              </div>

                              <div className="space-y-2" style={{ marginTop: '24px' }}>
                                <div className="flex items-center justify-between">
                                  <p
                                    className="text-[14px] font-inter leading-[20px] font-medium"
                                    style={{ color: '#4A5353' }}
                                  >
                                    {language && language?.['Asset Category']
                                      ? language?.['Asset Category']
                                      : 'Asset Category'}
                                  </p>
                                </div>
                                <div className="w-full">
                                  <div className="relative" ref={assetCategoryRef}>
                                    <input
                                      type="text"
                                      value={assetCategory}
                                      onChange={e => {
                                        setAssetCategory(e.target.value);
                                        setIsAssetCategoryDropdownOpen(true);
                                      }}
                                      onClick={() => {
                                        setIsAssetCategoryDropdownOpen(true);
                                      }}
                                      onKeyDown={e =>
                                        pickerKeyDown(e, 'assetCategory', isAssetCategoryDropdownOpen, setIsAssetCategoryDropdownOpen, listAssetCategory ?? [], handleAssetCategorySelect,
                                          category => category?.categoryName, formValues.assetCategoryId)
                                      }
                                      onBlur={() => setPickerHighlight({ key: '', index: -1 })}
                                      role="combobox"
                                      aria-expanded={isAssetCategoryDropdownOpen}
                                      style={{ pointerEvents: 'all' }}
                                      className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full`}
                                    />
                                    {errors.assetCategoryId && (
                                      <p className="error">{errors.assetCategoryId}</p>
                                    )}
                                    {isAssetCategoryDropdownOpen && (
                                      <div
                                        role="listbox"
                                        style={{ minHeight: loading.assetCategory ? '60px' : '0px' }}
                                        className="max-h-60 overflow-auto absolute z-[100] w-full mt-1 bg-white border border-[#EBEBED] rounded-lg p-1 shadow-extend"
                                      >
                                        <div className="w-full h-full relative">
                                          {listAssetCategory?.map((category, index) => (
                                            <div
                                              key={category.id}
                                              role="option"
                                              aria-selected={pickerHighlight.key === 'assetCategory' && pickerHighlight.index === index}
                                              style={{
                                                padding: '8px 6px',
                                                borderRadius: '4px',
                                                ...(pickerHighlight.key === 'assetCategory' && pickerHighlight.index === index ? { backgroundColor: '#E7F1F1' } : {}),
                                              }}
                                              className="cursor-pointer hover:bg-[#E7F1F1] text-main text-[14px] font-inter duration-300"
                                              onMouseDown={e => e.preventDefault()}
                                              onClick={() => handleAssetCategorySelect(category)}
                                            >
                                              {category.categoryName}
                                            </div>
                                          ))}
                                          {loading.assetCategory && (
                                            <div
                                              className="w-full h-full flex items-center justify-center"
                                              style={{ marginTop: '24px' }}
                                            >
                                              <span className="loader"></span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
                {
                  searchType !== 'Stock Item' && id !== 'new' && <>
                  {/* Simple price management on edit: current price + Change
                      Price (creates a new record server-side, history kept).
                      The full editable table stays available behind the
                      history toggle for power users. */}
                  <div style={{ maxWidth: '880px', margin: '24px auto 0' }}>
                    <div
                      style={{
                        padding: '24px',
                        border: '1px solid #DBE4E4',
                        borderRadius: '12px',
                        width: '100%',
                      }}
                    >
                      <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                          <p
                            className="font-inter"
                            style={{ fontSize: '16px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
                          >
                            {language && language?.['Selling Price'] ? language?.['Selling Price'] : 'Selling Price'}
                          </p>
                          <p
                            className="font-inter font-semibold"
                            style={{ fontSize: '24px', lineHeight: '32px', color: '#222525', marginTop: '4px' }}
                          >
                            {(() => {
                              const active = rows.filter(
                                (p: any) => p?.isActive !== false && p?.isDeleted !== true,
                              );
                              const now = Date.now();
                              const covering = active
                                .filter((p: any) => {
                                  const from = p.validFrom ? new Date(p.validFrom).getTime() : -Infinity;
                                  const to = p.validTo ? new Date(p.validTo).getTime() : Infinity;
                                  return from <= now && to >= now;
                                })
                                .sort(
                                  (a: any, b: any) =>
                                    new Date(b.validFrom ?? 0).getTime() - new Date(a.validFrom ?? 0).getTime(),
                                )[0];
                              const row = covering ?? active[active.length - 1];
                              const v = Number(row?.price);
                              return Number.isFinite(v) ? v.toFixed(2) : '—';
                            })()}
                          </p>
                        </div>
                        <div className="flex items-center" style={{ gap: '12px', flexWrap: 'wrap' }}>
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowChangePrice(true)}
                            onKeyDown={e => e.key === 'Enter' && setShowChangePrice(true)}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                              borderRadius: '40px',
                              padding: '10px 24px',
                              backgroundColor: '#0F7679',
                              color: '#FFFFFF',
                            }}
                          >
                            {language && language?.['Change Price'] ? language?.['Change Price'] : 'Change Price'}
                          </div>
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowPriceHistory(v => !v)}
                            onKeyDown={e => e.key === 'Enter' && setShowPriceHistory(v => !v)}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                              borderRadius: '40px',
                              padding: '10px 24px',
                              border: '1px solid #C9D7D8',
                              color: '#222525',
                            }}
                          >
                            {showPriceHistory
                              ? language && language?.['Hide price history']
                                ? language?.['Hide price history']
                                : 'Hide price history'
                              : language && language?.['Price history']
                                ? language?.['Price history']
                                : 'Price history'}
                          </div>
                        </div>
                      </div>
                      <p
                        className="text-[12px] font-inter leading-[16px]"
                        style={{ color: '#6A7878', marginTop: '8px' }}
                      >
                        {language && language?.['Changing the price keeps the full history of previous prices.']
                          ? language?.['Changing the price keeps the full history of previous prices.']
                          : 'Changing the price keeps the full history of previous prices.'}
                      </p>
                    </div>
                  </div>
                  </>
                }
                {
                  searchType !== 'Stock Item' && id !== 'new' && showPriceHistory && <>
 { rows.length > 0 ? (
                  <div
                    ref={priceSectionRef}
                    className="bg-white rounded-md w-full scroll-container"
                    style={{ border: isMobile ? 'none' : '1px solid #EBF0F0', maxHeight: "400px", overflowY: 'scroll', marginTop: '24px' }}>
                    {
                      !isMobile &&
                      <div className="w-full h-10 itmes-center gap-x-5" style={{ borderTopLeftRadius: '12px', borderTopRightRadius: '12px', }}>
                        <div className="flex items-center h-full"
                        >
                          <div
                            className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                            style={{
                              color: '#6A7878',
                              backgroundColor: "#EBF0F0",
                              paddingLeft: "10px",
                              width: `calc(100% / 8)`,
                            }}
                          >
                            <p>{language && language?.['Price'] ? language?.['Price'] : 'Price'}</p>
                          </div>
                          <div
                            className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                            style={{
                              color: '#6A7878',
                              backgroundColor: "#EBF0F0",
                              paddingLeft: "10px",
                              width: `calc(100% / 8)`,
                            }}
                          >
                            <p>{language && language?.['Cost'] ? language?.['Cost'] : 'Cost'}</p>
                          </div>
                          <div
                            className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                            style={{
                              color: '#6A7878',
                              backgroundColor: "#EBF0F0",
                              paddingLeft: "10px",
                              width: `calc(100% / 8)`,
                            }}
                          >
                            <p>{language && language?.['Price type'] ? language?.['Price type'] : 'Price type'}</p>
                          </div>
                          <div
                            className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                            style={{
                              color: '#6A7878',
                              backgroundColor: "#EBF0F0",
                              paddingLeft: "10px",
                              width: `calc(100% / 8)`,
                            }}
                          >
                            <p>{language && language?.['TAX'] ? language?.['TAX'] : 'TAX'}</p>
                          </div>
                          <div
                            className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                            style={{
                              color: '#6A7878',
                              backgroundColor: "#EBF0F0",
                              paddingLeft: "10px",
                              width: `calc(100% / 8)`,
                            }}
                          >
                            <p>
                              {language && language?.['Valid From']
                                ? language?.['Valid From']
                                : 'Valid From'}
                            </p>
                          </div>
                          <div
                            className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                            style={{
                              color: '#6A7878',
                              backgroundColor: "#EBF0F0",
                              paddingLeft: "10px",
                              width: `calc(100% / 8)`,
                            }}
                          >
                            <p>
                              {language && language?.['Valid To'] ? language?.['Valid To'] : 'Valid To'}
                            </p>
                          </div>
                          <div
                            className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                            style={{
                              color: '#6A7878',
                              backgroundColor: "#EBF0F0",
                              paddingLeft: "10px",
                              width: `calc(100% / 8)`,
                            }}
                          >
                            <p>{language && language?.['Status'] ? language?.['Status'] : 'Status'}</p>
                          </div>
                          <div
                            className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                            style={{
                              color: '#6A7878',
                              backgroundColor: "#EBF0F0",
                              paddingLeft: "10px",
                              width: `calc(100% / 8)`,
                            }}
                          >
                            <p>{language && language?.['Actions'] ? language?.['Actions'] : 'Actions'}</p>
                          </div>
                        </div>
                      </div>
                    }
                    {rows.map((row, index) => {
                      return (
                        <>
                          {
                            isMobile ?
                              <div key={row.id} style={{ border: '1px solid #DBE4E4', padding: '16px', borderRadius: "12px", marginBottom: index === rows.length - 1 ? '' : '12px' }}>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter"
                                  style={{
                                    color: "#4A5353",
                                    width: `100%`,
                                  }}
                                >
                                  <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                    {language && language?.['Price']
                                      ? language?.['Price']
                                      : 'Price'}
                                  </p>
                                  <input
                                    className="text-[14px] font-inter placeholder:text-placeholder text-placeholder outline-none focus-none bg-transparent w-full"
                                    style={{ padding: '12px', border: "1px solid #DBE4E4" }}
                                    value={row.price}
                                    onKeyDown={validateDecimalNumer}
                                    onBlur={e => handleBlurServiceChange(e, index, 'price')}
                                    onChange={e => handleInputServiceChange(e, index, 'price')}
                                  />
                                </div>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter"
                                  style={{
                                    color: "#4A5353",
                                    width: `100%`,
                                    margin: '12px 0px'
                                  }}
                                >
                                  <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                    {language && language?.['Cost']
                                      ? language?.['Cost']
                                      : 'Cost'}
                                  </p>
                                  <input
                                    className="text-[14px] font-inter placeholder:text-placeholder text-placeholder outline-none focus-none bg-transparent w-full"
                                    style={{ padding: '12px', border: "1px solid #DBE4E4" }}
                                    value={row.cost}
                                    onKeyDown={validateDecimalNumer}
                                    onBlur={e => handleBlurServiceChange(e, index, 'cost')}
                                    onChange={e => handleInputServiceChange(e, index, 'cost')}
                                  />
                                </div>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter"
                                  style={{
                                    color: "#4A5353",
                                    width: `100%`,
                                    margin: '12px 0px',
                                  }}
                                >
                                  <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                    {language && language?.['Price type']
                                      ? language?.['Price type']
                                      : 'Price type'}
                                  </p>
                                  <select
                                    className="text-[14px] font-inter outline-none bg-transparent w-full"
                                    style={{ padding: '12px', border: '1px solid #DBE4E4', borderRadius: 12 }}
                                    value={row.priceType ?? PriceType.Regular}
                                    onChange={(e) => {
                                      const value = Number(e.target.value);
                                      setRows((prev: any[]) =>
                                        prev.map((r, i) =>
                                          i === index ? { ...r, priceType: value } : r,
                                        ),
                                      );
                                    }}
                                  >
                                    {PRICE_TYPE_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {language && language?.[opt.name]
                                          ? language?.[opt.name]
                                          : opt.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter"
                                  style={{
                                    color: "#4A5353",
                                    width: `100%`,
                                  }}
                                >
                                  <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                    {language && language?.['TAX']
                                      ? language?.['TAX']
                                      : 'TAX'}
                                  </p>
                                  <div className="relative" ref={el => { if (el) dropdownRefs.current[index] = el; }}>
                                    <input
                                      ref={el => { taxInputRefs.current[index] = el; }}
                                      type="text"
                                      onKeyDown={e => {
                                        if (e.key !== 'Enter' && e.key !== 'Tab') validateNoSpecialChars(e);
                                        handleTaxInputKeyDown(e as any, index);
                                      }}
                                      value={searchTaxMap[index] !== undefined ? searchTaxMap[index] : ''}
                                      onChange={e => {
                                        const newValue = e.target.value;
                                        setSearchTaxMap((prev: any) => ({
                                          ...prev,
                                          [index]: newValue,
                                        }));
                                        setOpenDropdownIndexes(prev => ({ ...prev, [index]: true }));
                                      }}
                                      onClick={() => {
                                        handleTaxDropdownToggle(index);
                                      }}
                                      className="text-[14px] font-inter placeholder:text-placeholder text-placeholder outline-none focus-none bg-transparent w-full"
                                      style={{ padding: '12px', border: "1px solid #DBE4E4" }}
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center" style={{ margin: '12px 0px' }}>
                                  <div
                                    className="text-[14px] font-medium leading-[18px] font-inter"
                                    style={{
                                      color: "#4A5353",
                                      width: `50%`,
                                    }}
                                  >
                                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                      {language && language?.['Valid From']
                                        ? language?.['Valid From']
                                        : 'Valid From'}
                                    </p>
                                    <div className="w-full">
                                      <DatePicker
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText={
                                          language && language?.['Date valid from']
                                            ? language?.['Date valid from']
                                            : 'Date valid from'
                                        }
                                        name="startTime"
                                        className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center outline-none focus:none bg-transparent"
                                        selected={
                                          id === 'new'
                                            ? row.validFrom || null
                                            : row.validFrom
                                              ? new Date(row.validFrom)
                                              : null
                                        }
                                        onChange={e => handleSelectValidFrom(e, index)}
                                      // calendarContainer={CustomCalendarContainer}
                                      />
                                    </div>
                                  </div>
                                  <div
                                    className="text-[14px] font-medium leading-[18px] font-inter"
                                    style={{
                                      color: "#4A5353",
                                      width: `50%`,
                                    }}
                                  >
                                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                      {language && language?.['Valid To']
                                        ? language?.['Valid To']
                                        : 'Valid To'}
                                    </p>
                                    <div className="w-full">
                                      <DatePicker
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText={
                                          language && language?.['Date valid to']
                                            ? language?.['Date valid to']
                                            : 'Date valid to'
                                        }
                                        name="startTime"
                                        className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center outline-none focus:none bg-transparent"
                                        selected={row.validTo || null}
                                        onChange={e => handleSelectValidTo(e, index)}
                                      // calendarContainer={CustomCalendarContainer}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center">
                                  <div
                                    className="text-[14px] font-medium leading-[18px] font-inter"
                                    style={{
                                      width: `100%`,
                                    }}
                                  >
                                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                      {language && language?.['Status']
                                        ? language?.['Status']
                                        : 'Status'}
                                    </p>
                                    <div className="flex items-center gap-x-3">
                                      <div
                                        className={`switch ${row.isActive ? 'on' : 'off'}`}
                                        role="switch"
                                        aria-checked={!!row.isActive}
                                        aria-label={`${language?.['Status'] ?? 'Status'} ${language?.['Price'] ?? 'Price'} ${index + 1}`}
                                        tabIndex={0}
                                        onKeyDown={onToggleKey(() => handleToggle(index))}
                                        onClick={() => {
                                          handleToggle(index);
                                        }}
                                      >
                                        <div
                                          className={`shadow_toggle slider ${row.isActive ? 'move-right' : 'move-left'
                                            }`}
                                        ></div>
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    className="text-[14px] font-medium leading-[18px] font-inter"
                                    style={{
                                      width: `100%`,
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={language?.['Delete'] ?? 'Delete'}
                                    onKeyDown={onActivateKey(() => handleDelete(index))}
                                    onClick={() => handleDelete(index)}
                                  >
                                    <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                      {language && language?.['Action']
                                        ? language?.['Action']
                                        : 'Action'}
                                    </p>
                                    <RedDeleteIconV2 />
                                  </div>
                                </div>

                              </div>
                              :
                              <div key={row.id} className="w-full h-10 flex items-center bg-white" style={{ borderBottom: '1px solid #DBE4E4' }}>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                                  style={{
                                    color: "#4A5353",
                                    width: `calc(100% / 8)`,
                                    paddingLeft: "10px"
                                  }}
                                >
                                  <input
                                    className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center  outline-none focus:none bg-transparent"
                                    style={{ width: '100%', height: '22px' }}
                                    value={row.price}
                                    onKeyDown={validateDecimalNumer}
                                    onBlur={e => handleBlurServiceChange(e, index, 'price')}
                                    placeholder='Enter price'
                                    onChange={e => handleInputServiceChange(e, index, 'price')}
                                  />
                                </div>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                                  style={{
                                    color: "#4A5353",
                                    width: `calc(100% / 8)`,
                                    paddingLeft: "10px"
                                  }}
                                >
                                  <input
                                    className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center  outline-none focus:none bg-transparent"
                                    style={{ width: '100%', height: '22px' }}
                                    value={row.cost}
                                    onKeyDown={validateDecimalNumer}
                                    placeholder='Enter cost'
                                    onBlur={e => handleBlurServiceChange(e, index, 'cost')}
                                    onChange={e => handleInputServiceChange(e, index, 'cost')}
                                  />
                                </div>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                                  style={{
                                    color: "#4A5353",
                                    width: `calc(100% / 8)`,
                                    paddingLeft: "10px"
                                  }}
                                >
                                  {/* Price Type — controls which entry UI the
                                      POS shows at sale time. Default Regular
                                      from the row state literal. */}
                                  <select
                                    className="text-[14px] font-inter text-main outline-none bg-transparent"
                                    style={{ width: '100%', height: '22px', border: 'none' }}
                                    value={row.priceType ?? PriceType.Regular}
                                    onChange={(e) => {
                                      const value = Number(e.target.value);
                                      setRows((prev: any[]) =>
                                        prev.map((r, i) =>
                                          i === index ? { ...r, priceType: value } : r,
                                        ),
                                      );
                                    }}
                                  >
                                    {PRICE_TYPE_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {language && language?.[opt.name]
                                          ? language?.[opt.name]
                                          : opt.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                                  style={{
                                    color: "#4A5353",
                                    width: `calc(100% / 8)`,
                                    paddingLeft: "10px"
                                  }}
                                  ref={el => { if (el) dropdownRefs.current[index] = el; }}
                                >
                                  <div className="relative">
                                    <input
                                      ref={el => { taxInputRefs.current[index] = el; }}
                                      type="text"
                                      onKeyDown={e => {
                                        if (e.key !== 'Enter' && e.key !== 'Tab') validateNoSpecialChars(e);
                                        handleTaxInputKeyDown(e as any, index);
                                      }}
                                      placeholder='Enter TAX'
                                      value={searchTaxMap[index] !== undefined ? searchTaxMap[index] : ''}
                                      onChange={e => {
                                        const newValue = e.target.value;
                                        setSearchTaxMap((prev: any) => ({
                                          ...prev,
                                          [index]: newValue,
                                        }));
                                        setOpenDropdownIndexes(prev => ({ ...prev, [index]: true }));
                                      }}
                                      onClick={() => {
                                        handleTaxDropdownToggle(index);
                                      }}
                                      className={`text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center outline-none focus:none bg-transparent`}
                                    />
                                  </div>
                                </div>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                                  style={{
                                    color: "#4A5353",
                                    width: `calc(100% / 8)`,
                                    paddingLeft: "10px"
                                  }}
                                >
                                  <div className="w-full">
                                    <DatePicker
                                      dateFormat="dd-MM-yyyy"
                                      placeholderText={
                                        language && language?.['Date valid from']
                                          ? language?.['Date valid from']
                                          : 'Date valid from'
                                      }
                                      name="startTime"
                                      className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center outline-none focus:none bg-transparent"
                                      selected={
                                        id === 'new'
                                          ? row.validFrom || null
                                          : row.validFrom
                                            ? new Date(row.validFrom)
                                            : null
                                      }
                                      onChange={e => handleSelectValidFrom(e, index)}
                                    // calendarContainer={CustomCalendarContainer}
                                    />
                                  </div>
                                </div>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                                  style={{
                                    color: "#4A5353",
                                    width: `calc(100% / 8)`,
                                    paddingLeft: "10px"
                                  }}
                                >
                                  <div className="w-full">
                                    <DatePicker
                                      dateFormat="dd-MM-yyyy"
                                      placeholderText={
                                        language && language?.['Date valid to']
                                          ? language?.['Date valid to']
                                          : 'Date valid to'
                                      }
                                      name="startTime"
                                      className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center outline-none focus:none bg-transparent"
                                      selected={row.validTo || null}
                                      onChange={e => handleSelectValidTo(e, index)}
                                    // calendarContainer={CustomCalendarContainer}
                                    />
                                  </div>
                                </div>
                                <div
                                  className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                                  style={{
                                    color: "#4A5353",
                                    width: `calc(100% / 8)`,
                                    paddingLeft: "10px"
                                  }}
                                >
                                  <div className="flex items-center gap-x-3">
                                    <div
                                      className={`switch ${row.isActive ? 'on' : 'off'}`}
                                      role="switch"
                                      aria-checked={!!row.isActive}
                                      aria-label={`${language?.['Status'] ?? 'Status'} ${language?.['Price'] ?? 'Price'} ${index + 1}`}
                                      tabIndex={0}
                                      onKeyDown={onToggleKey(() => handleToggle(index))}
                                      onClick={() => {
                                        handleToggle(index);
                                      }}
                                    >
                                      <div
                                        className={`shadow_toggle slider ${row.isActive ? 'move-right' : 'move-left'
                                          }`}
                                      ></div>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="text-xs font-inter flex items-center cursor-pointer"
                                  style={{
                                    color: "#4A5353",
                                    width: `calc(100% / 8)`,
                                    paddingLeft: "10px"
                                  }}
                                  aria-label={language?.['Delete'] ?? 'Delete'}
                                  onClick={() => handleDelete(index)}
                                >
                                  <RedDeleteIconV2 />
                                </button>
                              </div>
                          }
                        </>
                      );
                    })}
                    {
                      isMobile ?
                        <button
                          type="button"
                          onClick={handleSelectType}
                          className="h-10 flex items-center relative cursor-pointer"
                          style={{ gap: '6px', padding: '0px 12px', width: 'fit-content', backgroundColor: '#EBF0F0', borderRadius: '12px', marginTop: '12px' }}
                        >
                          <div
                            className="flex items-center justify-center"
                            style={{ width: '24px', height: '24px' }}
                          >
                            <PlusGreenIcon />
                          </div>
                          <p
                            className="text-[14px] font-inter leading-[20px] font-medium"
                            style={{ color: '#0F7679 ' }}
                          >
                            {language && language?.['Add price'] ? language?.['Add price'] : 'Add price'}
                          </p>
                        </button>
                        :
                        <button
                          type="button"
                          onClick={handleSelectType}
                          className="h-10 flex items-center relative cursor-pointer"
                          style={{ gap: '6px', padding: '0px 12px', width: 'fit-content' }}
                        >
                          <div
                            className="flex items-center justify-center"
                            style={{ width: '24px', height: '24px' }}
                          >
                            <PlusGreenIcon />
                          </div>
                          <p
                            className="text-[14px] font-inter leading-[20px] font-medium"
                            style={{ color: '#0F7679 ' }}
                          >
                            {language && language?.['Add price'] ? language?.['Add price'] : 'Add price'}
                          </p>
                        </button>
                    }
                  </div>
                )
                  : (
                    <div className='text-center flex items-center justify-center' style={{ marginTop: "24px", padding: "24px", borderRadius: '12px', backgroundColor: '#F8F8F8' }}>
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <p
                            className="text-[14px] font-inter font-medium leading-[18px]"
                            style={{
                              color: '#222525',
                            }}
                          >
                            {
                              language && language?.['Item price']
                                ? language?.['Item price']
                                : 'Item price'
                            }
                          </p>
                          <p
                            className="text-[14px] font-inter font-medium leading-[18px]"
                            style={{
                              color: '#4A5353',
                            }}
                          >
                            {
                              language && language?.['No item prices added yet.']
                                ? language?.['No item prices added yet.']
                                : 'No item prices added yet.'
                            }
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleSelectType}
                          className="h-10 flex items-center relative cursor-pointer"
                          style={{ gap: '6px', padding: '0px 12px', width: 'fit-content', margin: '0 auto' }}
                        >
                          <div
                            className="flex items-center justify-center"
                            style={{ width: '24px', height: '24px' }}
                          >
                            <PlusGreenIcon />
                          </div>
                          <p
                            className="text-[14px] font-inter leading-[20px] font-medium"
                            style={{ color: '#0F7679 ' }}
                          >
                            {language && language?.['Add price'] ? language?.['Add price'] : 'Add price'}
                          </p>
                        </button>
                      </div>
                    </div>
                  )}
                  </>
                }
               
              </>
            )
            :
            <>
            </>
        }
      </div >
      <div
        className="w-full flex items-center justify-end shadow_button relative"
        style={{ gap: "12px", paddingTop: "24px", }}
      >
        <div className="absolute" style={{ left: "-24px", top: 0, height: "1px", backgroundColor: "#DBE4E4", width: "calc(100% + 48px)" }}></div>
        <button
          type="button"
          {...kb.stepProps(handleBackNavigation)}
          style={{
            padding: '10px 24px',
            borderRadius: '30px',
            color: '#222525',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid #C9D7D8',
          }}
          className="font-inter cursor-pointer"
        >
          {step === 1 ?
            language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'
            :
            language && language?.['Back'] ? language?.['Back'] : 'Back'
          }
        </button>

        {/* Distinct keys: a focused Next must not stay focused as the Save button. */}
        {step === 1 ? (
          <button
            key="next"
            type="button"
            {...kb.primaryProps(() => {
              // Nếu là Asset hoặc Stock Item thì submit luôn
              if (searchType === 'Asset' || searchType === 'Stock Item') {
                return handleSubmit({ preventDefault: () => { } } as any);
              } else {
                // Don't let users carry missing required basics into Step 2 —
                // surface the errors next to their fields instead.
                const step1Errors = collectStep1Errors();
                if (Object.keys(step1Errors).length > 0) {
                  setErrors(step1Errors);
                  return;
                }
                setStep(2);
              }
            })}
            style={{
              padding: '10px 24px',
              borderRadius: '30px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: '#0F7679',
            }}
            className="font-inter cursor-pointer"
          >
            {searchType === 'Asset' || searchType === 'Stock Item'
              ? (language && language?.['Save changes'] ? language?.['Save changes'] : 'Save changes')
              : (language && language?.['Next'] ? language?.['Next'] : 'Next')
            }
          </button>
        ) : (
          <button
            key="save"
            type="button"
            {...kb.primaryProps((e: any) => handleSubmit(e))}
            style={{
              padding: '10px 24px',
              borderRadius: '30px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: '#0F7679',
            }}
            className="font-inter cursor-pointer"
          >
            {language && language?.['Save changes'] ? language?.['Save changes'] : 'Save changes'}
          </button>
        )}
      </div>
      <div
        className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showPopupImage ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        ref={filesDialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={language?.['View Files'] ?? 'View Files'}
        style={{
          backgroundColor: 'rgba(0,0,0,0.8)',
          zIndex: '200',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
          width: '100%',
          visibility: showPopupImage ? 'visible' : 'hidden',
        }}
      >
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showPopupImage ? 'translate-y-0' : 'translate-y-4 opacity-0'
            }`}
          style={{
            borderRadius: '6px',
            backgroundColor: 'white',
            width: '80%',
            height: '90%',
            transition: 'transform 0.3s ease, opacity 0.3s ease',
            overflow: 'hidden',
          }}
        >
          <div
            className="flex items-center justify-between"
            style={{
              padding: '12px 16px',
              backgroundColor: '#F7F7F8',
              borderBottom: '1px solid #EBEBED',
            }}
          >
            <p className="text-[16px] font-semibold font-inter text-main leading-[24px]"></p>
            <button
              type="button"
              aria-label={language?.['Close'] ?? 'Close'}
              onClick={() => {
                setShowPopupImage(false);
              }}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
              }}
            >
              <CloseIcon />
            </button>
          </div>
          <div
            className="flex items-center justify-center"
            style={{ padding: '20px 16px', height: 'calc(100% - 48px)' }}
          >
            {(previewImg || formValues?.image) && (
              <img
                src={previewImg ? previewImg : formValues?.image}
                alt="Uploaded preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: '4px',
                }}
              />
            )}
          </div>
        </div>
      </div>
      <PopupLocation
        show={showPopup}
        title={`${id === 'new'
          ? language && language?.['Create Items']
            ? language?.['Create Items']
            : 'Create Items'
          : language && language?.['Update Items']
            ? language?.['Update Items']
            : 'Update Items'
          }`}
        desc={
          errors.missingFields
            ? errors.missingFields
            : id === 'new'
              ? language?.['Create Items Failed'] ?? 'Create Items Failed'
              : language?.['Update Items Failed'] ?? 'Update Items Failed'
        }
        onClose={handleClosePopup}
        onOpen={handleOpenPopup}
      />
      <div
        className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showAlt ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        ref={altDialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={language?.['Alternative UOM'] ?? 'Alternative UOM'}
        style={{
          backgroundColor: '#22252552',
          zIndex: '200',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
          width: '100vw',
          height: '100vh',
          left: 0,
          top: 0,
          visibility: showAlt ? 'visible' : 'hidden',
        }}
      >
        <div {...altUomKb.scopeProps}>
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showAlt ? 'translate-y-0' : 'translate-y-4 opacity-0'
            }`}
          style={{
            padding: '24px',
            borderRadius: '24px',
            backgroundColor: '#FFFFFF',
            width: '530px',
            transition: 'transform 0.3s ease, opacity 0.3s ease',
          }}
        >
          <div className="flex items-start justify-between">
            <div>
              <p
                style={{ fontSize: '20px', color: '#222525' }}
                className="font-semibold font-inter leading-[24px]"
              >
                {language && language?.['Alternative UOM']
                  ? language?.['Alternative UOM']
                  : 'Alternative UOM'}
              </p>
            </div>
            <button
              type="button"
              aria-label={language?.['Close'] ?? 'Close'}
              onClick={() => {
                handleCancelAlternativeUOM();
              }}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
              }}
            >
              <CloseIcon />
            </button>
          </div>
          <div className="" style={{ marginTop: '12px' }}>
            <div className="w-full space-y-2" style={{ marginTop: '24px', width: '100%' }}>
              <div className="flex items-center">
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: '#4A5353' }}
                >
                  {language && language?.['UOM type'] ? language?.['UOM type'] : 'UOM type'}
                </p>
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: '#BA0E13' }}
                >
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="relative">
                {/* Same picker as the base UOM: filters the loaded list as you type. */}
                <DropdownSearch<{ id: string; uomName: string }>
                  refKey={`UOMAlt`}
                  refs={{
                    [`UOMAlt`]: dropdownUOMAltRef,
                  }}
                  openStates={{
                    [`UOMAlt`]: isUOMAltDropdownOpen,
                  }}
                  open={() => setIsUOMAltDropdownOpen(true)}
                  close={() => setIsUOMAltDropdownOpen(false)}
                  data={listUOMAlt || []}
                  labelExtractor={item => item.uomName}
                  value={searchUOMAlt || ''}
                  setValue={setSearchUOMAlt}
                  onSelect={handleUOMSelectAlt}
                  loading={loading.uom}
                  borderNone={false}
                />
                {errors.baseUOMId && <p className="error">{errors.baseUOMId}</p>}
              </div>
            </div>
            <div
              style={{
                marginTop: '20px',
              }}
            >
              <div>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    marginTop: '12px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={alternativeId.allowOrdering}
                    onChange={() => handleCheckboxAltChange('allowOrdering')}
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      accentColor: '#0F7679',
                    }}
                  />
                  <span
                    style={{
                      fontSize: '14px',
                      color: '#222525',
                      fontWeight: '500',
                      fontFamily: 'Inter',
                    }}
                  >
                    Order
                  </span>
                </label>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    marginTop: '12px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={alternativeId.allowRecipe}
                    onChange={() => handleCheckboxAltChange('allowRecipe')}
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      accentColor: '#0F7679',
                    }}
                  />
                  <span
                    style={{
                      fontSize: '14px',
                      color: '#222525',
                      fontWeight: '500',
                      fontFamily: 'Inter',
                    }}
                  >
                    Receipt
                  </span>
                </label>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
            <button
              type="button"
              onClick={() => {
                handleCancelAlternativeUOM();
              }}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '40px',
                padding: '10px 24px',
                border: '1px solid #C9D7D8',
                color: '#222525',
              }}
            >
              {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
            </button>
            <button
              type="button"
              {...altUomKb.primaryProps(handleAddAlternativeUOM)}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '40px',
                padding: '10px 24px',
                backgroundColor: '#0F7679',
                color: '#FFFFFF',
              }}
            >
              {language && language?.['Save'] ? language?.['Save'] : 'Save'}
            </button>
          </div>
        </div>
        </div>
      </div>
      <div
        className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        ref={deleteDialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={language?.['Delete alternative UOM?'] ?? 'Delete alternative UOM?'}
        style={{
          backgroundColor: '#22252552',
          zIndex: '200',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
          width: '100vw',
          height: '100vh',
          left: 0,
          top: 0,
          visibility: showDelete ? 'visible' : 'hidden',
        }}
      >
        <div {...deleteUomKb.scopeProps}>
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showDelete ? 'translate-y-0' : 'translate-y-4 opacity-0'
            }`}
          style={{
            padding: '24px',
            borderRadius: '24px',
            backgroundColor: '#FFFFFF',
            width: '530px',
            transition: 'transform 0.3s ease, opacity 0.3s ease',
          }}
        >
          <div className="flex items-center justify-between">
            <p
              style={{ fontSize: '20px', color: '#222525' }}
              className="font-semibold font-inter leading-[24px]"
            >
              {language && language?.['Delete alternative UOM?']
                ? language?.['Delete alternative UOM?']
                : 'Delete alternative UOM?'}
            </p>
            <button
              type="button"
              aria-label={language?.['Close'] ?? 'Close'}
              onClick={() => {
                setShowDelete(false);
                setIdSelect('');
              }}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
              }}
            >
              <CloseIcon />
            </button>
          </div>
          <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
            <p
              className="text-[14px] font-inter leading-[24px]"
              style={{ color: '#6A7878' }}
            >
              {language &&
                language?.[
                'This alternative UOM user will be permanently deleted. Are you sure you want to continue'
                ]
                ? language?.[
                'This alternative UOM user will be permanently deleted. Are you sure you want to continue'
                ]
                : 'This alternative UOM user will be permanently deleted. Are you sure you want to continue'}
            </p>
          </div>
          <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
            <button
              type="button"
              onClick={() => {
                setShowDelete(false);
                setIdSelect('');
              }}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '40px',
                padding: '10px 24px',
                border: '1px solid #C9D7D8',
                color: '#222525',
              }}
            >
              {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
            </button>
            <button
              type="button"
              {...deleteUomKb.primaryProps(() => {
                setFormValues((prev: any) => {
                  return {
                    ...prev,
                    alternativeIds:
                      prev.alternativeIds?.filter((v: any) => {
                        return v.UOMId !== idSelect;
                      }) || [],
                  };
                });
                setShowDelete(false);
              })}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '40px',
                padding: '10px 24px',
                backgroundColor: '#BA0E13',
                color: '#FFFFFF',
              }}
            >
              {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
            </button>
          </div>
        </div>
        </div>
      </div>
      {taxDropdownPosition &&
        createPortal(
          <div
            ref={taxDropdownPortalRef}
            className="bg-white rounded-lg max-h-60 overflow-auto p-1 shadow-extend border border-[#EBEBED]"
            style={{
              position: 'fixed',
              top: taxDropdownPosition.top + 4,
              left: taxDropdownPosition.left,
              width: taxDropdownPosition.width,
              minHeight: loading.tax ? 60 : undefined,
              zIndex: 9999,
            }}
          >
            {getFilteredTaxList(taxDropdownPosition.index).map((tax: any) => (
              <div
                key={tax.id}
                className="px-4 py-2 cursor-pointer text-main text-[14px] font-inter hover:bg-[#E7F1F1]"
                onClick={() => handleTaxSelect(tax, taxDropdownPosition.index)}
              >
                {tax.groupName ?? ''}
              </div>
            ))}
            {getFilteredTaxList(taxDropdownPosition.index).length === 0 && !loading.tax && (
              <div className="px-4 py-2 text-[14px] text-[#6A7878]">No match</div>
            )}
            {loading.tax && (
              <div className="w-full flex items-center justify-center" style={{ marginTop: '24px' }}>
                <span className="loader"></span>
              </div>
            )}
          </div>,
          document.body
        )}
      {/* Edit mode: shared simple Change Price dialog; refresh the loaded
          product (incl. price rows) after a successful change. */}
      {id !== 'new' && (
        <ChangePriceDialog
          isOpen={showChangePrice}
          onClose={() => setShowChangePrice(false)}
          onSaved={() => fetchData(id)}
          productId={id}
          productName={formValues?.productName}
          currentPrice={(() => {
            const active = rows.filter((p: any) => p?.isActive !== false && p?.isDeleted !== true);
            const now = Date.now();
            const covering = active
              .filter((p: any) => {
                const from = p.validFrom ? new Date(p.validFrom).getTime() : -Infinity;
                const to = p.validTo ? new Date(p.validTo).getTime() : Infinity;
                return from <= now && to >= now;
              })
              .sort(
                (a: any, b: any) =>
                  new Date(b.validFrom ?? 0).getTime() - new Date(a.validFrom ?? 0).getTime(),
              )[0];
            const row = covering ?? active[active.length - 1];
            const v = Number(row?.price);
            return Number.isFinite(v) ? v : null;
          })()}
          language={language}
        />
      )}
    </div>
  );
};
export default CreateItems;
